/**
 * PixelArtGenerator.ts
 * Zero Missing Assets Architecture with Dual-Canvas 4x Super-Sampled HiDPI Rendering.
 * Synthesizes high-craft 2.5D isometric Penthouse Loft textures onto 4x HD backing buffers,
 * downsamples to logical canvases preserving 100% test assertion dimensions, and registers
 * rich vector illustrations directly into Phaser's Texture Manager.
 */

import Phaser from './MicroCanvasEngine';

/**
 * Core 32-Color Palette (Contract Invariant: exactly 32 keys, # or rgba strings).
 * Required by tier5_m2_isometric_stress.test.js:238-246.
 */
export const PALETTE = {
  transparent: 'rgba(0,0,0,0)',
  deepInk: '#13101d',        // Outlines, deep shadows
  darkEspresso: '#24171e',   // Dark wood grain, bed frame
  richMahogany: '#42242e',   // Furniture shadow, hair dark
  warmWalnut: '#633833',     // Wood parquet dark, desk surface
  classicOak: '#8a583e',     // Standard wood, floor planks
  goldenAmber: '#b88148',    // Wood highlight, desk top
  warmBirch: '#e3b16d',      // Light wood, floor highlight
  paleCream: '#fae29c',      // Pillows, day tint, star
  pureLinen: '#ffffff',      // Sheet highlight, specular dots
  deepSteel: '#1e232d',      // PC frame, modern fridge sides
  coolCharcoal: '#333c4a',   // Electronics, keyboard, iron
  midNavy: '#485a6c',        // Jeans, rug border, night sky
  slateGray: '#677f91',      // Stainless fridge, metal legs
  paleSilver: '#9cb4bf',     // Screen glass, highlights
  frostWhite: '#e2edf2',     // Window glass highlight, tile
  midnightIndigo: '#161329', // Night sky, deep shadow
  twilightPurple: '#39224f', // Sunset sky, sofa velvet
  royalPlum: '#683569',      // Tier 3 smart quilt
  roseMauve: '#a8526a',      // Cushions, carpet border
  sunsetCoral: '#de6f65',    // Sunset window, hoodie
  goldenSunshine: '#f5a842', // Sun orb, daylight glow
  lemonGlow: '#fcd853',      // Lamp glow, brass buckle
  forestMoss: '#223b28',     // Plant darks, pot shadows
  oliveFern: '#3b6b3e',      // Plant leaves
  vividGrass: '#65a854',     // Leaf highlights
  mintSprout: '#a2db79',     // Sprout, LED neon
  oceanTeal: '#276e72',      // Cyber workstation glow
  neonCyan: '#45c3b8',       // Smart screens, cyber trim
  peachSkin: '#e8a87c',      // Chibi skin
  softBlush: '#cf7b67',      // Skin blush, lips
  rubyRed: '#b82d38',        // Accent red, power LED
};

/**
 * Extended HiDPI Luxury Penthouse Materials Palette.
 * Kept separate to preserve PALETTE's strict 32-key invariant.
 */
export const HD_PALETTE = {
  blackSteel: '#12141a',
  charcoalFrame: '#1a1d24',
  concreteLight: '#303440',
  concreteBase: '#232630',
  concreteDark: '#171922',
  baseboardWood: '#3a221b',
  moldingGold: '#d4af37',

  marbleWhite: '#f8fafc',
  marbleSoftGray: '#e2e8f0',
  marbleVeinGray: '#94a3b8',
  marbleVeinGold: '#d97706',
  marbleGrout: '#475569',

  herringboneOakLight: '#b58655',
  herringboneOakBase: '#936439',
  herringboneOakDark: '#6c4425',
  herringboneBevel: '#462c18',

  rugField: '#2b1b3f',
  rugBorder: '#4c3069',
  rugLavender: '#a78bfa',

  skyDayTop: '#38bdf8',
  skyDayBottom: '#bae6fd',
  skySunsetTop: '#4c1d95',
  skySunsetMid: '#db2777',
  skySunsetBottom: '#f97316',
  skyNightTop: '#030712',
  skyNightBottom: '#0f172a',

  windowGlowYellow: '#fef08a',
  windowGlowAmber: '#fbbf24',
  beaconRed: '#ef4444',

  velvetPlum: '#581c87',
  velvetPlumLight: '#7e22ce',
  cashmereIvory: '#fffbeb',

  cyberNeonCyan: '#06b6d4',
  cyberGlow: 'rgba(6, 182, 212, 0.35)',
  rgbMagenta: '#d946ef',
  brushedSteelLight: '#94a3b8',
  brushedSteelBase: '#64748b',
  brushedSteelDark: '#334155',
  brassGold: '#eab308',

  terracottaBase: '#c2410c',
  terracottaRim: '#ea580c',
  monsteraDark: '#14532d',
  monsteraMid: '#16a34a',
  monsteraBright: '#4ade80',
};

// Safe gradient helper
function createLinear(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  stops: Array<[number, string]>,
  fallback: string
): string | CanvasGradient {
  try {
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    if (g && g.addColorStop) {
      for (const [offset, color] of stops) {
        g.addColorStop(offset, color);
      }
      return g;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

function createRadial(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  r0: number,
  x1: number,
  y1: number,
  r1: number,
  stops: Array<[number, string]>,
  fallback: string
): string | CanvasGradient {
  try {
    if (typeof ctx.createRadialGradient === 'function') {
      const g = ctx.createRadialGradient(x0, y0, r0, x1, y1, r1);
      if (g && g.addColorStop) {
        for (const [offset, color] of stops) {
          g.addColorStop(offset, color);
        }
        return g;
      }
    }
    return fallback;
  } catch {
    return fallback;
  }
}

function drawPoly(
  ctx: CanvasRenderingContext2D,
  pts: Array<[number, number]>,
  fill?: string | CanvasGradient | null,
  stroke?: string | null,
  lineWidth: number = 4
): void {
  if (!pts.length) return;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(pts[i][0], pts[i][1]);
  }
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();
  }
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  lineWidth: number = 4
): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.stroke();
}

function drawEllipse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  fill?: string | CanvasGradient | null,
  stroke?: string | null,
  lineWidth: number = 4
): void {
  ctx.beginPath();
  if (typeof ctx.ellipse === 'function') {
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  } else {
    ctx.arc(cx, cy, Math.min(rx, ry), 0, Math.PI * 2);
  }
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }
}

function drawIsoPrism(
  ctx: CanvasRenderingContext2D,
  cx: number,
  topY: number,
  rx: number,
  ry: number,
  height: number,
  leftColor: string,
  topColor: string,
  rightColor: string,
  stroke: string,
  lineWidth: number = 4
): void {
  // Left face
  drawPoly(
    ctx,
    [
      [cx - rx, topY],
      [cx, topY + ry],
      [cx, topY + ry + height],
      [cx - rx, topY + height],
    ],
    leftColor,
    stroke,
    lineWidth
  );
  // Right face
  drawPoly(
    ctx,
    [
      [cx, topY + ry],
      [cx + rx, topY],
      [cx + rx, topY + height],
      [cx, topY + ry + height],
    ],
    rightColor,
    stroke,
    lineWidth
  );
  // Top diamond face
  drawPoly(
    ctx,
    [
      [cx, topY - ry],
      [cx + rx, topY],
      [cx, topY + ry],
      [cx - rx, topY],
    ],
    topColor,
    stroke,
    lineWidth
  );
}

export class PixelArtGenerator {
  /**
   * Safe helper to create an HTMLCanvasElement.
   */
  public static createCanvas(width: number, height: number): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      return canvas;
    } catch {
      return null;
    }
  }

  /**
   * Dual-Canvas 4x Super-Sampling Pattern:
   * 1. Draws rich anti-aliased vector artwork at 4x resolution onto an HD canvas.
   * 2. Downsamples onto a logical canvas matching unit test assertions.
   * 3. Attaches __hdCanvas and __scale to the logical canvas for runtime rendering up to 2.2x+ zoom.
   */
  public static createSuperSampledCanvas(
    logicalW: number,
    logicalH: number,
    scale: number,
    drawFn: (ctx: CanvasRenderingContext2D, w: number, h: number, s: number) => void
  ): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    try {
      const hdW = logicalW * scale;
      const hdH = logicalH * scale;

      const hdCanvas = document.createElement('canvas');
      hdCanvas.width = hdW;
      hdCanvas.height = hdH;
      const hdCtx = hdCanvas.getContext('2d');
      if (hdCtx) {
        hdCtx.imageSmoothingEnabled = true;
        if ('imageSmoothingQuality' in hdCtx) {
          hdCtx.imageSmoothingQuality = 'high';
        }
        drawFn(hdCtx, hdW, hdH, scale);
      }

      const logicalCanvas = document.createElement('canvas');
      logicalCanvas.width = logicalW;
      logicalCanvas.height = logicalH;
      const logicalCtx = logicalCanvas.getContext('2d');
      if (logicalCtx) {
        logicalCtx.imageSmoothingEnabled = true;
        if ('imageSmoothingQuality' in logicalCtx) {
          logicalCtx.imageSmoothingQuality = 'high';
        }
        logicalCtx.drawImage(hdCanvas, 0, 0, logicalW, logicalH);
      }

      (logicalCanvas as any).__hdCanvas = hdCanvas;
      (logicalCanvas as any).__scale = scale;

      return logicalCanvas;
    } catch {
      return null;
    }
  }

  // =========================================================================
  // FLOOR TILES (40x20 Logical -> 160x80 HD)
  // =========================================================================

  public static generateWoodFloor(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(40, 20, 4, (ctx, w, h, s) => {
      // 2:1 Isometric Diamond (160x80)
      ctx.beginPath();
      ctx.moveTo(80, 0);
      ctx.lineTo(159.5, 39.75);
      ctx.lineTo(80, 79.5);
      ctx.lineTo(0.5, 39.75);
      ctx.closePath();

      // Warm Softened Honey-Walnut Floor Base (muted contrast so character & furniture pop clearly)
      ctx.fillStyle = createLinear(
        ctx,
        0,
        0,
        w,
        h,
        [
          [0, '#a6754f'],
          [0.5, '#966642'],
          [1, '#855837'],
        ],
        PALETTE.warmWalnut
      );
      ctx.fill();

      ctx.save();
      ctx.clip();

      // Soft, low-contrast warm oak plank strips (gentle transitions, no harsh glare)
      const plankCount = 6;
      for (let i = -plankCount; i < plankCount * 2; i++) {
        const py = i * 14;
        const isAlt = Math.abs(i) % 2 === 0;
        ctx.fillStyle = isAlt ? 'rgba(255, 228, 196, 0.06)' : 'rgba(42, 24, 12, 0.06)';
        ctx.fillRect(0, py, w, 12.5);

        // Ultra-subtle plank seam
        ctx.fillStyle = 'rgba(58, 34, 18, 0.14)';
        ctx.fillRect(0, py + 12.5, w, 1.2);

        // Delicate organic wood grain whisper
        ctx.fillStyle = 'rgba(68, 38, 20, 0.08)';
        ctx.fillRect(22 + ((i * 35) % 96), py + 4, 36, 1.2);
        ctx.fillRect(82 + ((i * 25) % 56), py + 8, 28, 1.2);
      }

      ctx.restore();

      // Soft, muted perimeter bevel so diagonal grid lines remain calm and cozy
      ctx.strokeStyle = 'rgba(255, 232, 198, 0.15)';
      ctx.lineWidth = 1.0 * s;
      ctx.beginPath();
      ctx.moveTo(0.5, 39.75);
      ctx.lineTo(80, 0);
      ctx.lineTo(159.5, 39.75);
      ctx.stroke();

      ctx.strokeStyle = 'rgba(45, 25, 14, 0.18)';
      ctx.lineWidth = 0.9 * s;
      ctx.beginPath();
      ctx.moveTo(0.5, 39.75);
      ctx.lineTo(80, 79.5);
      ctx.lineTo(159.5, 39.75);
      ctx.stroke();
    });
  }

  public static generateCarpetFloor(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(40, 20, 4, (ctx, w, h, s) => {
      // 2:1 Isometric Diamond (160x80)
      ctx.beginPath();
      ctx.moveTo(80, 0);
      ctx.lineTo(159.5, 39.75);
      ctx.lineTo(80, 79.5);
      ctx.lineTo(0.5, 39.75);
      ctx.closePath();

      // Scandinavian Geometric Tufted Rug
      ctx.fillStyle = createLinear(
        ctx,
        0,
        0,
        w,
        h,
        [
          [0, HD_PALETTE.rugBorder],
          [0.5, HD_PALETTE.rugField],
          [1, PALETTE.midnightIndigo],
        ],
        PALETTE.twilightPurple
      );
      ctx.fill();

      ctx.save();
      ctx.clip();

      // Outer woven diamond border
      ctx.beginPath();
      ctx.moveTo(80, 8);
      ctx.lineTo(144, 39.75);
      ctx.lineTo(80, 71.5);
      ctx.lineTo(16, 39.75);
      ctx.closePath();
      ctx.strokeStyle = PALETTE.roseMauve;
      ctx.lineWidth = 2.5 * s;
      ctx.stroke();

      // Inner geometric diamond
      ctx.beginPath();
      ctx.moveTo(80, 18);
      ctx.lineTo(128, 39.75);
      ctx.lineTo(80, 61.5);
      ctx.lineTo(32, 39.75);
      ctx.closePath();
      ctx.strokeStyle = HD_PALETTE.rugLavender;
      ctx.lineWidth = 1.5 * s;
      ctx.stroke();

      // Center tufted diamond motif
      ctx.fillStyle = PALETTE.royalPlum;
      ctx.beginPath();
      ctx.moveTo(80, 26);
      ctx.lineTo(108, 39.75);
      ctx.lineTo(80, 53.5);
      ctx.lineTo(52, 39.75);
      ctx.closePath();
      ctx.fill();

      // Ivory woven loop-pile dots
      ctx.fillStyle = PALETTE.paleCream;
      const pts = [
        [72, 39.75],
        [80, 34],
        [80, 45.5],
        [88, 39.75],
      ];
      for (const [px, py] of pts) {
        ctx.beginPath();
        ctx.arc(px, py, 2 * s, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    });
  }

  public static generateCeramicFloor(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(40, 20, 4, (ctx, w, h, s) => {
      // 2:1 Isometric Diamond (160x80)
      ctx.beginPath();
      ctx.moveTo(80, 0);
      ctx.lineTo(159.5, 39.75);
      ctx.lineTo(80, 79.5);
      ctx.lineTo(0.5, 39.75);
      ctx.closePath();

      // Calacatta Polished Marble Base
      ctx.fillStyle = createLinear(
        ctx,
        0,
        0,
        w,
        h,
        [
          [0, HD_PALETTE.marbleWhite],
          [0.5, HD_PALETTE.marbleSoftGray],
          [1, '#cbd5e1'],
        ],
        PALETTE.slateGray
      );
      ctx.fill();

      ctx.save();
      ctx.clip();

      // Delicate organic marble veining (soft grey and subtle gold)
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.45)';
      ctx.lineWidth = 2 * s;
      ctx.beginPath();
      ctx.moveTo(20, 15);
      ctx.bezierCurveTo(45, 25, 70, 18, 110, 45);
      ctx.bezierCurveTo(125, 55, 140, 60, 150, 70);
      ctx.stroke();

      ctx.strokeStyle = 'rgba(217, 119, 6, 0.35)';
      ctx.lineWidth = 1 * s;
      ctx.beginPath();
      ctx.moveTo(50, 60);
      ctx.bezierCurveTo(70, 50, 95, 45, 120, 25);
      ctx.stroke();

      // 4-quadrant diamond grout lines
      ctx.strokeStyle = HD_PALETTE.marbleGrout;
      ctx.lineWidth = 1 * s;
      ctx.beginPath();
      ctx.moveTo(80, 0);
      ctx.lineTo(80, 79.5);
      ctx.moveTo(0.5, 39.75);
      ctx.lineTo(159.5, 39.75);
      ctx.stroke();

      // Specular corner sheen / gloss reflection
      ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
      ctx.beginPath();
      ctx.moveTo(35, 18);
      ctx.lineTo(55, 18);
      ctx.lineTo(40, 26);
      ctx.lineTo(20, 26);
      ctx.closePath();
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(105, 50);
      ctx.lineTo(125, 50);
      ctx.lineTo(115, 55);
      ctx.lineTo(95, 55);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    });
  }

  // =========================================================================
  // ISOMETRIC WALLS (160x160 Logical -> 640x640 HD)
  // =========================================================================

  public static generateBackLeftWall(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(160, 160, 4, (ctx, w, h, s) => {
      // 2:1 Isometric Parallelogram (Left Wall)
      // (0, 320) -> (640, 0) -> (640, 320) -> (0, 640)
      ctx.beginPath();
      ctx.moveTo(0, 320);
      ctx.lineTo(640, 0);
      ctx.lineTo(640, 320);
      ctx.lineTo(0, 640);
      ctx.closePath();

      // Warm Cozy Linen-Cocoa Wallpaper Gradient
      ctx.fillStyle = createLinear(
        ctx,
        0,
        160,
        640,
        480,
        [
          [0, '#5c4338'],
          [0.55, '#4b362d'],
          [1, '#3d2b24'],
        ],
        '#4b362d'
      );
      ctx.fill();

      ctx.save();
      ctx.clip();

      // Subtle Cozy Striped Wallpaper Pattern (Warm Cream & Golden-Taupe Vertical Stripes)
      for (let x = 0; x < w; x += 14 * s) {
        ctx.fillStyle = 'rgba(254, 243, 199, 0.065)';
        ctx.fillRect(x, 0, 6 * s, h);
        ctx.fillStyle = 'rgba(251, 191, 36, 0.05)';
        ctx.fillRect(x + 7 * s, 0, 1.2 * s, h);
        ctx.fillStyle = 'rgba(19, 16, 29, 0.14)';
        ctx.fillRect(x + 12.5 * s, 0, 1 * s, h);
      }

      // Lower Wooden Wainscoting Paneling Band along bottom 42px
      ctx.beginPath();
      ctx.moveTo(0, 640);
      ctx.lineTo(640, 320);
      ctx.lineTo(640, 320 - 40 * s);
      ctx.lineTo(0, 640 - 40 * s);
      ctx.closePath();
      ctx.fillStyle = createLinear(
        ctx,
        0,
        640,
        640,
        240,
        [
          [0, '#452719'],
          [1, '#341d12'],
        ],
        '#3d2216'
      );
      ctx.fill();

      // Wainscoting chair rail molding
      ctx.beginPath();
      ctx.moveTo(0, 640 - 40 * s);
      ctx.lineTo(640, 320 - 40 * s);
      ctx.lineTo(640, 320 - 36.5 * s);
      ctx.lineTo(0, 640 - 36.5 * s);
      ctx.closePath();
      ctx.fillStyle = '#b45309';
      ctx.fill();

      // Warm Timber Crown Molding along top edge
      ctx.beginPath();
      ctx.moveTo(0, 320);
      ctx.lineTo(640, 0);
      ctx.lineTo(640, 10 * s);
      ctx.lineTo(0, 320 + 10 * s);
      ctx.closePath();
      ctx.fillStyle = '#78350f';
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(0, 320 + 10 * s);
      ctx.lineTo(640, 10 * s);
      ctx.lineTo(640, 12.5 * s);
      ctx.lineTo(0, 320 + 12.5 * s);
      ctx.closePath();
      ctx.fillStyle = '#f59e0b';
      ctx.fill();

      // Rich Hardwood Baseboard Molding along bottom slope
      ctx.beginPath();
      ctx.moveTo(0, 640);
      ctx.lineTo(640, 320);
      ctx.lineTo(640, 320 - 18 * s);
      ctx.lineTo(0, 640 - 18 * s);
      ctx.closePath();
      ctx.fillStyle = createLinear(
        ctx,
        0,
        640,
        640,
        320,
        [
          [0, '#78350f'],
          [1, '#451a03'],
        ],
        PALETTE.warmWalnut
      );
      ctx.fill();

      // Baseboard beveled golden-oak top lip
      ctx.beginPath();
      ctx.moveTo(0, 640 - 18 * s);
      ctx.lineTo(640, 320 - 18 * s);
      ctx.lineTo(640, 320 - 15 * s);
      ctx.lineTo(0, 640 - 15 * s);
      ctx.closePath();
      ctx.fillStyle = '#f59e0b';
      ctx.fill();

      // Ambient occlusion corner shadow where wall joins the floor
      ctx.beginPath();
      ctx.moveTo(0, 640);
      ctx.lineTo(640, 320);
      ctx.lineTo(640, 320 - 4 * s);
      ctx.lineTo(0, 640 - 4 * s);
      ctx.closePath();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.38)';
      ctx.fill();

      ctx.restore();
    });
  }

  public static generateBackRightWall(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(160, 160, 4, (ctx, w, h, s) => {
      // 2:1 Isometric Parallelogram (Right Wall)
      // (0, 0) -> (640, 320) -> (640, 640) -> (0, 320)
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(640, 320);
      ctx.lineTo(640, 640);
      ctx.lineTo(0, 320);
      ctx.closePath();

      // Warm Cozy Linen-Cocoa Wallpaper (Right shaded facet)
      ctx.fillStyle = createLinear(
        ctx,
        0,
        0,
        640,
        480,
        [
          [0, '#4f3930'],
          [0.55, '#412e27'],
          [1, '#33231d'],
        ],
        '#412e27'
      );
      ctx.fill();

      ctx.save();
      ctx.clip();

      // Subtle Cozy Striped Wallpaper Pattern
      for (let x = 0; x < w; x += 14 * s) {
        ctx.fillStyle = 'rgba(254, 243, 199, 0.055)';
        ctx.fillRect(x, 0, 6 * s, h);
        ctx.fillStyle = 'rgba(251, 191, 36, 0.04)';
        ctx.fillRect(x + 7 * s, 0, 1.2 * s, h);
        ctx.fillStyle = 'rgba(19, 16, 29, 0.16)';
        ctx.fillRect(x + 12.5 * s, 0, 1 * s, h);
      }

      // Lower Wooden Wainscoting Paneling Band along bottom 40px
      ctx.beginPath();
      ctx.moveTo(0, 320);
      ctx.lineTo(640, 640);
      ctx.lineTo(640, 640 - 40 * s);
      ctx.lineTo(0, 320 - 40 * s);
      ctx.closePath();
      ctx.fillStyle = '#361f14';
      ctx.fill();

      // Wainscoting chair rail molding
      ctx.beginPath();
      ctx.moveTo(0, 320 - 40 * s);
      ctx.lineTo(640, 640 - 40 * s);
      ctx.lineTo(640, 640 - 36.5 * s);
      ctx.lineTo(0, 320 - 36.5 * s);
      ctx.closePath();
      ctx.fillStyle = '#92400e';
      ctx.fill();

      // Warm Timber Crown Molding along top edge
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(640, 320);
      ctx.lineTo(640, 320 + 10 * s);
      ctx.lineTo(0, 10 * s);
      ctx.closePath();
      ctx.fillStyle = '#5c280b';
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(0, 10 * s);
      ctx.lineTo(640, 320 + 10 * s);
      ctx.lineTo(640, 320 + 12.5 * s);
      ctx.lineTo(0, 12.5 * s);
      ctx.closePath();
      ctx.fillStyle = '#d97706';
      ctx.fill();

      // Rich Hardwood Baseboard Molding along bottom slope
      ctx.beginPath();
      ctx.moveTo(0, 320);
      ctx.lineTo(640, 640);
      ctx.lineTo(640, 640 - 18 * s);
      ctx.lineTo(0, 320 - 18 * s);
      ctx.closePath();
      ctx.fillStyle = '#451a03';
      ctx.fill();

      // Baseboard lip
      ctx.beginPath();
      ctx.moveTo(0, 320 - 18 * s);
      ctx.lineTo(640, 640 - 18 * s);
      ctx.lineTo(640, 640 - 15 * s);
      ctx.lineTo(0, 320 - 15 * s);
      ctx.closePath();
      ctx.fillStyle = '#d97706';
      ctx.fill();

      // Ambient occlusion corner shadow where wall joins the floor
      ctx.beginPath();
      ctx.moveTo(0, 320);
      ctx.lineTo(640, 640);
      ctx.lineTo(640, 640 - 4 * s);
      ctx.lineTo(0, 320 - 4 * s);
      ctx.closePath();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.42)';
      ctx.fill();

      ctx.restore();
    });
  }

  // =========================================================================
  // ISOMETRIC COZY WOODEN WINDOWS WITH CURTAINS (48x44 Logical -> 192x176 HD)
  // Skewed to the exact 2:1 Left-Wall Isometric Slope (-0.5 dy/dx)
  // =========================================================================

  private static drawIsometricCozyWindow(
    ctx: CanvasRenderingContext2D,
    s: number,
    phase: 'day' | 'sunset' | 'night'
  ): void {
    const outline = '#13101d';
    // Isometric parallelogram vertices inside 48x44 logical box:
    // Left stile x = 4, Right stile x = 44 (width dx = 40 -> dy = -20 along 2:1 isometric slope)
    // Left top: (4, 21), Right top: (44, 1)
    // Right bottom: (44, 23), Left bottom: (4, 43)

    // 1. Deep Exterior Recessed Wall Shadow
    drawPoly(
      ctx,
      [
        [3.5 * s, 21.2 * s],
        [44.5 * s, 0.7 * s],
        [44.5 * s, 23.8 * s],
        [3.5 * s, 44.0 * s],
      ],
      'rgba(15, 23, 42, 0.45)'
    );

    // 2. Outer Carved Warm Oak Wooden Frame
    const frameGrad = createLinear(
      ctx,
      4 * s,
      2 * s,
      44 * s,
      42 * s,
      [
        [0, '#d97706'],
        [0.5, '#92400e'],
        [1, '#451a03'],
      ],
      PALETTE.warmWalnut
    );
    drawPoly(
      ctx,
      [
        [4 * s, 21 * s],
        [44 * s, 1 * s],
        [44 * s, 23 * s],
        [4 * s, 43 * s],
      ],
      frameGrad,
      outline,
      1.2 * s
    );

    // Inner recessed wood bevel
    drawPoly(
      ctx,
      [
        [6.5 * s, 20.8 * s],
        [41.5 * s, 3.3 * s],
        [41.5 * s, 21.8 * s],
        [6.5 * s, 39.3 * s],
      ],
      '#451a03',
      outline,
      0.9 * s
    );

    // 3. Sky Backdrop Clipped to Isometric Glass Opening
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(7.5 * s, 20.5 * s);
    ctx.lineTo(40.5 * s, 4.0 * s);
    ctx.lineTo(40.5 * s, 21.2 * s);
    ctx.lineTo(7.5 * s, 37.7 * s);
    ctx.closePath();
    ctx.clip();

    if (phase === 'day') {
      ctx.fillStyle = createLinear(
        ctx,
        0,
        4 * s,
        0,
        38 * s,
        [
          [0, '#0284c7'],
          [0.55, '#38bdf8'],
          [1, '#fef9c3'],
        ],
        '#38bdf8'
      );
      ctx.fillRect(0, 0, 48 * s, 44 * s);

      // Glowing Sun & Soft Halo
      ctx.fillStyle = 'rgba(254, 240, 138, 0.45)';
      ctx.beginPath();
      ctx.arc(17 * s, 21 * s, 6.5 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.arc(17 * s, 21 * s, 3.8 * s, 0, Math.PI * 2);
      ctx.fill();

      // Fluffy Clouds
      ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
      ctx.beginPath();
      ctx.arc(28 * s, 16 * s, 2.8 * s, 0, Math.PI * 2);
      ctx.arc(31.5 * s, 14.5 * s, 3.6 * s, 0, Math.PI * 2);
      ctx.arc(35 * s, 15.2 * s, 2.5 * s, 0, Math.PI * 2);
      ctx.fill();
    } else if (phase === 'sunset') {
      ctx.fillStyle = createLinear(
        ctx,
        0,
        4 * s,
        0,
        38 * s,
        [
          [0, '#311042'],
          [0.45, '#be185d'],
          [0.75, '#f97316'],
          [1, '#fde047'],
        ],
        '#f97316'
      );
      ctx.fillRect(0, 0, 48 * s, 44 * s);

      // Setting Golden Sun
      ctx.fillStyle = 'rgba(253, 224, 71, 0.4)';
      ctx.beginPath();
      ctx.arc(26 * s, 21 * s, 7 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.arc(26 * s, 21 * s, 4.2 * s, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Sparkling Night Sky Gradient
      ctx.fillStyle = createLinear(
        ctx,
        0,
        4 * s,
        0,
        38 * s,
        [
          [0, '#090d1e'],
          [0.5, '#1e1b4b'],
          [1, '#312e81'],
        ],
        '#1e1b4b'
      );
      ctx.fillRect(0, 0, 48 * s, 44 * s);

      // Twinkling Starfield & 4-Point Sparkle Stars
      const stars = [
        [13, 22, 0.9],
        [18, 17, 1.1],
        [23, 19, 0.8],
        [27, 13, 1.2],
        [35, 11, 0.9],
        [15, 28, 0.8],
        [22, 24, 1.0],
        [31, 18, 0.85],
      ];
      for (const [sx, sy, sr] of stars) {
        ctx.fillStyle = 'rgba(254, 240, 138, 0.35)';
        ctx.beginPath();
        ctx.arc(sx * s, sy * s, sr * 2.1 * s, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(sx * s, sy * s, sr * s, 0, Math.PI * 2);
        ctx.fill();
      }

      // Glowing Crescent Moon with Lunar Halo
      ctx.fillStyle = 'rgba(254, 249, 195, 0.28)';
      ctx.beginPath();
      ctx.arc(31 * s, 13.5 * s, 5.8 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.arc(31 * s, 13.5 * s, 3.6 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#141538';
      ctx.beginPath();
      ctx.arc(29.6 * s, 12.6 * s, 3.1 * s, 0, Math.PI * 2);
      ctx.fill();
    }

    // Distant Cozy Rooftops / Skyline Silhouettes along bottom slope
    const roofs = [
      { x: 9, w: 6, h: 8 },
      { x: 15, w: 7, h: 11 },
      { x: 22, w: 6, h: 9 },
      { x: 28, w: 7, h: 12 },
      { x: 35, w: 6, h: 8 },
    ];
    for (const r of roofs) {
      const baseSlopeY = 37.7 - ((r.x - 7.5) / 33) * 16.5;
      ctx.fillStyle = phase === 'day' ? '#1e293b' : '#0f172a';
      ctx.fillRect(r.x * s, (baseSlopeY - r.h) * s, r.w * s, (r.h + 8) * s);
      // Warm glowing tiny apartment windows
      ctx.fillStyle = phase === 'day' ? 'rgba(255,255,255,0.35)' : '#fde047';
      ctx.fillRect((r.x + 1.4) * s, (baseSlopeY - r.h + 2) * s, 1.3 * s, 1.6 * s);
      ctx.fillRect((r.x + 3.6) * s, (baseSlopeY - r.h + 4.2) * s, 1.3 * s, 1.6 * s);
    }

    // Diagonal Specular Glass Glaze Streaks
    ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
    drawPoly(ctx, [
      [14 * s, 17 * s],
      [19 * s, 14.5 * s],
      [13 * s, 34 * s],
      [9 * s, 36 * s],
    ]);

    // 4. Wooden Cross Mullions (Vertical + Isometric Slope Transom)
    drawLine(ctx, 24 * s, 12.2 * s, 24 * s, 29.5 * s, '#78350f', 1.8 * s);
    drawLine(ctx, 24 * s, 12.2 * s, 24 * s, 29.5 * s, '#f59e0b', 0.7 * s);
    drawLine(ctx, 7.5 * s, 29.1 * s, 40.5 * s, 12.6 * s, '#78350f', 1.8 * s);
    drawLine(ctx, 7.5 * s, 28.8 * s, 40.5 * s, 12.3 * s, '#f59e0b', 0.7 * s);

    // 5. Cozy Draped Linen Curtains Tied Back on Both Sides (with Golden Tie-Backs)
    const curtainGrad = createLinear(
      ctx,
      7 * s,
      4 * s,
      41 * s,
      38 * s,
      [
        [0, '#fffbeb'],
        [0.5, '#fde68a'],
        [1, '#f59e0b'],
      ],
      '#fef3c7'
    );
    // Left draped curtain panel
    drawPoly(
      ctx,
      [
        [7.5 * s, 20.5 * s],
        [15.5 * s, 16.5 * s],
        [12.5 * s, 27.5 * s],
        [14.2 * s, 34.4 * s],
        [7.5 * s, 37.7 * s],
      ],
      curtainGrad,
      outline,
      0.95 * s
    );
    drawLine(ctx, 7.5 * s, 29.2 * s, 12.8 * s, 27.2 * s, '#d97706', 1.3 * s);

    // Right draped curtain panel
    drawPoly(
      ctx,
      [
        [32.5 * s, 8.0 * s],
        [40.5 * s, 4.0 * s],
        [40.5 * s, 21.2 * s],
        [34.2 * s, 24.4 * s],
        [35.8 * s, 17.0 * s],
      ],
      curtainGrad,
      outline,
      0.95 * s
    );
    drawLine(ctx, 35.5 * s, 17.0 * s, 40.5 * s, 14.8 * s, '#d97706', 1.3 * s);

    ctx.restore();

    // 6. Top Wooden Curtain Rod & Brass Finials
    drawLine(ctx, 2.5 * s, 20.5 * s, 45.5 * s, -1.0 * s, outline, 2.1 * s);
    drawLine(ctx, 2.5 * s, 20.5 * s, 45.5 * s, -1.0 * s, '#fbbf24', 1.1 * s);
    ctx.fillStyle = '#fde047';
    ctx.strokeStyle = outline;
    ctx.lineWidth = 0.85 * s;
    ctx.beginPath();
    ctx.arc(3.2 * s, 20.2 * s, 1.5 * s, 0, Math.PI * 2);
    ctx.arc(44.8 * s, 0.8 * s, 1.5 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // 7. Chunky Protruding Isometric Wooden Window Sill along Bottom Slope
    drawPoly(
      ctx,
      [
        [2.5 * s, 42.2 * s],
        [44.5 * s, 21.2 * s],
        [46.5 * s, 22.4 * s],
        [4.5 * s, 43.4 * s],
      ],
      '#f59e0b',
      outline,
      1.1 * s
    );
    drawPoly(
      ctx,
      [
        [4.5 * s, 43.4 * s],
        [46.5 * s, 22.4 * s],
        [46.5 * s, 24.0 * s],
        [4.5 * s, 44.0 * s],
      ],
      '#78350f',
      outline,
      1.0 * s
    );
  }

  public static generateWindowDay(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(48, 44, 4, (ctx, _w, _h, s) => {
      this.drawIsometricCozyWindow(ctx, s, 'day');
    });
  }

  public static generateWindowSunset(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(48, 44, 4, (ctx, _w, _h, s) => {
      this.drawIsometricCozyWindow(ctx, s, 'sunset');
    });
  }

  public static generateWindowNight(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(48, 44, 4, (ctx, _w, _h, s) => {
      this.drawIsometricCozyWindow(ctx, s, 'night');
    });
  }

  // =========================================================================
  // ANIMATED CHIBI CHARACTER SPRITESHEET (96x96 Logical -> 384x384 HD)
  // =========================================================================

  public static generateCharacterSpritesheet(): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(96, 96, 4, (ctx, _w, _h, s) => {
      // 4 directions (rows) x 6 walk cycle frames (cols)
      // Row 0: SE (facing down-right)
      // Row 1: SW (facing down-left)
      // Row 2: NE (facing up-right)
      // Row 3: NW (facing up-left)
      for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 6; col++) {
          const frameX = col * 16 * s;
          const frameY = row * 24 * s;

          const isBob = col === 1 || col === 3 || col === 5;
          const bobOffset = isBob ? 0.85 * s : 0;
          const legPhase = col >= 2 ? (col - 2) % 4 : -1;

          this.drawChibiVectorFrame(ctx, frameX, frameY, row, bobOffset, legPhase, s);
        }
      }
    });
  }

  private static drawChibiVectorFrame(
    ctx: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    directionRow: number,
    bob: number,
    legPhase: number,
    s: number
  ): void {
    const outline = '#3D251E'; // Warm espresso-brown ink outline matching Good Pizza Great Pizza / Unpacking furniture
    const isFacingDown = directionRow === 0 || directionRow === 1;
    const isFacingRight = directionRow === 0 || directionRow === 2;
    const dirShift = isFacingRight ? 0.65 * s : -0.65 * s;

    ctx.save();
    ctx.beginPath();
    ctx.rect(ox, oy, 16 * s, 24 * s);
    ctx.clip();

    const centerX = ox + 8 * s;
    const headY = oy + 2.2 * s + bob;
    const torsoY = oy + 11.2 * s + bob;
    const legY = oy + 17.2 * s;

    // 0. Back Hair Layer (warm chestnut gouache behind head & shoulders)
    ctx.fillStyle = '#6e3b23';
    ctx.strokeStyle = outline;
    ctx.lineWidth = 1.05 * s;
    ctx.beginPath();
    ctx.arc(centerX - dirShift * 0.3, headY + 5.2 * s, 6.7 * s, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // 1. Dynamic Legs & Cozy Brown Leather Loafers
    const drawLegAndLoafer = (lx: number, ly: number, strideY: number) => {
      // Soft Slate-Sage Denim Trousers
      const denimGrad = createLinear(
        ctx,
        lx,
        ly + strideY,
        lx + 3 * s,
        ly + strideY + 4.4 * s,
        [
          [0, '#7c96a8'],
          [0.5, '#647d8f'],
          [1, '#4f6575'],
        ],
        '#647d8f'
      );
      ctx.beginPath();
      ctx.roundRect(lx, ly + strideY, 3 * s, 4.3 * s, [0.9 * s]);
      ctx.fillStyle = denimGrad;
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.95 * s;
      ctx.stroke();

      // Soft rolled trouser cuff
      ctx.fillStyle = '#9cb2c2';
      ctx.fillRect(lx + 0.3 * s, ly + strideY + 3.1 * s, 2.4 * s, 0.9 * s);

      // Warm Chestnut-Brown Leather Loafer
      const shoeX = isFacingRight ? lx - 0.2 * s : lx - 0.5 * s;
      const shoeY = ly + strideY + 3.9 * s;
      ctx.beginPath();
      ctx.roundRect(shoeX, shoeY, 3.7 * s, 2.2 * s, [0.9 * s]);
      ctx.fillStyle = '#8c5332';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.95 * s;
      ctx.stroke();

      // Darker oak outsole trim
      ctx.fillStyle = '#5c341d';
      ctx.fillRect(shoeX + 0.4 * s, shoeY + 0.95 * s, 2.8 * s, 0.65 * s);
    };

    const leftStride = legPhase === 0 ? 0.5 * s : legPhase === 2 ? -0.9 * s : 0;
    const rightStride = legPhase === 2 ? 0.5 * s : legPhase === 0 ? -0.9 * s : 0;
    drawLegAndLoafer(centerX - 3.6 * s, legY, leftStride);
    drawLegAndLoafer(centerX + 0.6 * s, legY, rightStride);

    // 2. Back Arm (swings opposite to leg stride)
    const armSwingL = legPhase === 0 ? -0.8 * s : legPhase === 2 ? 0.8 * s : 0;
    const armSwingR = -armSwingL;

    const drawCardiganSleeve = (ax: number, ay: number) => {
      // Cozy cream cable-knit cardigan sleeve
      ctx.beginPath();
      ctx.roundRect(ax, ay, 2.3 * s, 4.6 * s, [1.1 * s]);
      ctx.fillStyle = '#fdf6e2';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.95 * s;
      ctx.stroke();

      // Pastel terracotta ribbed cuff band
      ctx.fillStyle = '#e07a5f';
      ctx.fillRect(ax + 0.25 * s, ay + 3.2 * s, 1.8 * s, 0.8 * s);

      // Chibi Hand
      ctx.beginPath();
      ctx.arc(ax + 1.15 * s, ay + 4.9 * s, 1.05 * s, 0, Math.PI * 2);
      ctx.fillStyle = '#fbd3b7';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.85 * s;
      ctx.stroke();
    };

    if (isFacingRight) {
      drawCardiganSleeve(centerX - 6.6 * s, torsoY + 0.6 * s + armSwingL);
    } else {
      drawCardiganSleeve(centerX + 4.3 * s, torsoY + 0.6 * s + armSwingR);
    }

    // 3. Cozy Cream & Pastel Terracotta Knit Cardigan Torso
    const cardiganGrad = createLinear(
      ctx,
      centerX - 5 * s,
      torsoY,
      centerX + 5 * s,
      torsoY + 6.6 * s,
      [
        [0, '#fff9eb'],
        [0.5, '#f7ebd2'],
        [1, '#ead6b3'],
      ],
      '#f7ebd2'
    );
    ctx.beginPath();
    ctx.roundRect(centerX - 4.9 * s, torsoY, 9.8 * s, 6.5 * s, [2.1 * s]);
    ctx.fillStyle = cardiganGrad;
    ctx.fill();
    ctx.strokeStyle = outline;
    ctx.lineWidth = 1.05 * s;
    ctx.stroke();

    if (isFacingDown) {
      // Soft butter-yellow inner tee V-panel
      ctx.fillStyle = '#fae596';
      ctx.fillRect(centerX - 1.9 * s + dirShift * 0.35, torsoY + 0.4 * s, 3.8 * s, 5.6 * s);

      // Pastel terracotta shawl collar & button placket seams
      drawLine(
        ctx,
        centerX - 1.9 * s + dirShift * 0.35,
        torsoY + 0.3 * s,
        centerX - 1.9 * s + dirShift * 0.35,
        torsoY + 6.1 * s,
        '#e07a5f',
        1.1 * s
      );
      drawLine(
        ctx,
        centerX + 1.9 * s + dirShift * 0.35,
        torsoY + 0.3 * s,
        centerX + 1.9 * s + dirShift * 0.35,
        torsoY + 6.1 * s,
        '#e07a5f',
        1.1 * s
      );

      // Wooden cardigan buttons
      ctx.fillStyle = '#8c5332';
      ctx.beginPath();
      ctx.arc(centerX - 1.4 * s + dirShift * 0.35, torsoY + 2.8 * s, 0.45 * s, 0, Math.PI * 2);
      ctx.arc(centerX - 1.4 * s + dirShift * 0.35, torsoY + 4.3 * s, 0.45 * s, 0, Math.PI * 2);
      ctx.fill();

      // Pastel terracotta ribbed hem trim
      ctx.fillStyle = '#e07a5f';
      ctx.fillRect(centerX - 4.2 * s, torsoY + 5.4 * s, 8.4 * s, 0.8 * s);
    } else {
      // Back view: Pastel terracotta knit collar trim
      ctx.beginPath();
      ctx.roundRect(centerX - 3.6 * s, torsoY + 0.3 * s, 7.2 * s, 2.2 * s, [1.1 * s]);
      ctx.fillStyle = '#e07a5f';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.85 * s;
      ctx.stroke();
    }

    // Front Arm
    if (isFacingRight) {
      drawCardiganSleeve(centerX + 4.3 * s, torsoY + 0.6 * s + armSwingR);
    } else {
      drawCardiganSleeve(centerX - 6.6 * s, torsoY + 0.6 * s + armSwingL);
    }

    // 4. Sculpted Chibi Head with Warm Gouache Skin & Espresso-Brown Ink Contour
    const faceGrad = createLinear(
      ctx,
      centerX - 6 * s,
      headY,
      centerX + 6 * s,
      headY + 10 * s,
      [
        [0, '#fff3e6'],
        [0.6, '#fbd3b7'],
        [1, '#ebae88'],
      ],
      '#fbd3b7'
    );
    ctx.beginPath();
    ctx.arc(centerX + dirShift * 0.2, headY + 5.3 * s, 6.1 * s, 0, Math.PI * 2);
    ctx.fillStyle = faceGrad;
    ctx.fill();
    ctx.strokeStyle = outline;
    ctx.lineWidth = 1.05 * s;
    ctx.stroke();

    // 5. Friendly Cozy Face (when facing SE / SW)
    if (isFacingDown) {
      const eyeOffsetX = isFacingRight ? 0.75 * s : -0.75 * s;
      const eye1X = centerX - 3.1 * s + eyeOffsetX;
      const eye2X = centerX + 1.2 * s + eyeOffsetX;
      const eyeY = headY + 4.6 * s;

      // Soft Peach-Rose Gouache Cheek Blushes
      ctx.fillStyle = 'rgba(224, 122, 95, 0.48)';
      ctx.beginPath();
      ctx.ellipse(eye1X - 0.3 * s, eyeY + 2.8 * s, 1.55 * s, 0.95 * s, 0, 0, Math.PI * 2);
      ctx.ellipse(eye2X + 2.0 * s, eyeY + 2.8 * s, 1.55 * s, 0.95 * s, 0, 0, Math.PI * 2);
      ctx.fill();

      // Warm Espresso-Brown Bean Eyes with Soft Cream Catchlight
      for (const ex of [eye1X, eye2X]) {
        ctx.beginPath();
        ctx.roundRect(ex, eyeY, 1.85 * s, 2.45 * s, [0.9 * s]);
        ctx.fillStyle = outline;
        ctx.fill();

        ctx.fillStyle = '#fff5e4';
        ctx.beginPath();
        ctx.arc(ex + 0.65 * s, eyeY + 0.75 * s, 0.45 * s, 0, Math.PI * 2);
        ctx.fill();
      }

      // Warm chestnut eyebrows & gentle smile
      drawLine(ctx, eye1X + 0.1 * s, eyeY - 0.7 * s, eye1X + 1.8 * s, eyeY - 0.55 * s, '#6e3b23', 0.75 * s);
      drawLine(ctx, eye2X + 0.1 * s, eyeY - 0.55 * s, eye2X + 1.8 * s, eyeY - 0.7 * s, '#6e3b23', 0.75 * s);

      ctx.beginPath();
      ctx.arc(centerX + eyeOffsetX * 0.7, eyeY + 2.95 * s, 0.95 * s, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.75 * s;
      ctx.stroke();
    }

    // 6. Layered Warm Chestnut Hair Crown & Soft Highlights
    const hairGrad = createLinear(
      ctx,
      centerX,
      headY - 2 * s,
      centerX,
      headY + 8 * s,
      [
        [0, '#b5673f'],
        [0.5, '#8f4d2c'],
        [1, '#6e3b23'],
      ],
      '#8f4d2c'
    );

    if (isFacingDown) {
      drawPoly(
        ctx,
        [
          [centerX - 6.6 * s, headY + 6.4 * s],
          [centerX - 6.9 * s, headY + 2.2 * s],
          [centerX - 4.5 * s, headY - 0.6 * s],
          [centerX, headY - 1.3 * s],
          [centerX + 4.5 * s, headY - 0.6 * s],
          [centerX + 6.9 * s, headY + 2.2 * s],
          [centerX + 6.6 * s, headY + 6.4 * s],
          [centerX + 4.8 * s, headY + 3.2 * s],
          [centerX + 2.1 * s, headY + 4.2 * s],
          [centerX + 0.4 * s, headY + 2.5 * s],
          [centerX - 1.5 * s, headY + 4.3 * s],
          [centerX - 4.7 * s, headY + 3.1 * s],
        ],
        hairGrad,
        outline,
        1.0 * s
      );

      // Soft warm honey highlight band
      ctx.fillStyle = 'rgba(245, 198, 132, 0.45)';
      ctx.beginPath();
      ctx.ellipse(centerX - 1.5 * s, headY + 1.1 * s, 2.2 * s, 0.6 * s, -0.18, 0, Math.PI * 2);
      ctx.ellipse(centerX + 2.2 * s, headY + 1.2 * s, 1.6 * s, 0.5 * s, 0.2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      drawPoly(
        ctx,
        [
          [centerX - 6.7 * s, headY + 8.4 * s],
          [centerX - 6.9 * s, headY + 2.0 * s],
          [centerX - 4.4 * s, headY - 0.8 * s],
          [centerX, headY - 1.4 * s],
          [centerX + 4.4 * s, headY - 0.8 * s],
          [centerX + 6.9 * s, headY + 2.0 * s],
          [centerX + 6.7 * s, headY + 8.4 * s],
          [centerX + 3.6 * s, headY + 9.4 * s],
          [centerX, headY + 8.7 * s],
          [centerX - 3.6 * s, headY + 9.4 * s],
        ],
        hairGrad,
        outline,
        1.0 * s
      );

      ctx.fillStyle = 'rgba(245, 198, 132, 0.38)';
      ctx.beginPath();
      ctx.ellipse(centerX, headY + 2.2 * s, 3.6 * s, 0.75 * s, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Expressive Top Hair Tuft
    drawPoly(
      ctx,
      [
        [centerX - 0.4 * s, headY - 1.1 * s],
        [centerX + (isFacingRight ? 2.4 : -2.4) * s, headY - 2.3 * s],
        [centerX + (isFacingRight ? 1.0 : -1.0) * s, headY - 0.6 * s],
      ],
      '#9c5533',
      outline,
      0.85 * s
    );

    ctx.restore();
  }

  // =========================================================================
  // MULTI-TIER 2:1 ISOMETRIC BEDS (Tiers 1-3)
  // =========================================================================

  public static generateBedT1(): HTMLCanvasElement | null {
    // 1. bed_t1: Giường Gỗ Ấm Cúng / Cozy Wooden Bed (44x26 Logical -> 176x104 HD)
    // True 2.5D isometric warm oak bed frame with carved wooden headboard, plump pillows,
    // warm quilted comforter, and attached bedside nightstand + glowing amber lamp.
    return this.createSuperSampledCanvas(44, 26, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Isometric Shadow
      drawPoly(
        ctx,
        [
          [15 * s, 6.2 * s],
          [42.8 * s, 17.6 * s],
          [27 * s, 25.6 * s],
          [1.2 * s, 14.5 * s],
        ],
        'rgba(15, 23, 42, 0.44)'
      );

      // Attached Bedside Nightstand & Glowing Warm Amber Lamp (Back-Right beside Headboard)
      const lampHalo = createRadial(
        ctx,
        22.5 * s,
        3.6 * s,
        0.4 * s,
        22.5 * s,
        3.6 * s,
        5.0 * s,
        [
          [0, 'rgba(254, 240, 138, 0.95)'],
          [0.6, 'rgba(245, 158, 11, 0.45)'],
          [1, 'rgba(245, 158, 11, 0)'],
        ],
        'rgba(254, 240, 138, 0.48)'
      );
      ctx.fillStyle = lampHalo;
      ctx.beginPath();
      ctx.arc(22.5 * s, 3.6 * s, 5.0 * s, 0, Math.PI * 2);
      ctx.fill();

      drawPoly(
        ctx,
        [
          [19.5 * s, 5.6 * s],
          [25.5 * s, 8.2 * s],
          [22.5 * s, 9.8 * s],
          [16.5 * s, 7.2 * s],
        ],
        '#d97706',
        outline,
        0.95 * s
      );
      drawPoly(
        ctx,
        [
          [22.5 * s, 9.8 * s],
          [25.5 * s, 8.2 * s],
          [25.5 * s, 12.2 * s],
          [22.5 * s, 13.8 * s],
        ],
        '#78350f',
        outline,
        0.95 * s
      );
      drawLine(ctx, 22.5 * s, 4.4 * s, 22.5 * s, 7.2 * s, '#fbbf24', 1.2 * s);
      drawPoly(
        ctx,
        [
          [20.2 * s, 4.4 * s],
          [21.2 * s, 1.8 * s],
          [23.8 * s, 1.8 * s],
          [24.8 * s, 4.4 * s],
        ],
        '#fef08a',
        outline,
        0.95 * s
      );

      // Carved Warm Walnut & Oak Wooden Headboard along Back-Left Head Edge
      drawPoly(
        ctx,
        [
          [2.0 * s, 6.8 * s],
          [15.8 * s, 0.8 * s],
          [17.6 * s, 1.6 * s],
          [3.8 * s, 7.6 * s],
        ],
        '#d97706',
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [2.0 * s, 6.8 * s],
          [3.8 * s, 7.6 * s],
          [3.8 * s, 13.6 * s],
          [2.0 * s, 12.8 * s],
        ],
        '#451a03',
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [3.8 * s, 7.6 * s],
          [17.6 * s, 1.6 * s],
          [17.6 * s, 7.8 * s],
          [3.8 * s, 13.6 * s],
        ],
        PALETTE.warmWalnut,
        outline,
        1.15 * s
      );
      // Recessed wooden headboard panels
      drawPoly(
        ctx,
        [
          [5.2 * s, 8.2 * s],
          [10.2 * s, 6.0 * s],
          [10.2 * s, 9.6 * s],
          [5.2 * s, 11.8 * s],
        ],
        PALETTE.classicOak,
        PALETTE.darkEspresso,
        0.85 * s
      );
      drawPoly(
        ctx,
        [
          [11.4 * s, 5.4 * s],
          [16.2 * s, 3.3 * s],
          [16.2 * s, 6.9 * s],
          [11.4 * s, 9.0 * s],
        ],
        PALETTE.classicOak,
        PALETTE.darkEspresso,
        0.85 * s
      );

      // Slatted Blonde Birch & Oak Wooden Bed Base (Top + Left Bevel + Right Bevel)
      const palletGrad = createLinear(
        ctx,
        2 * s,
        6 * s,
        42 * s,
        24 * s,
        [
          [0, '#fde68a'],
          [0.55, PALETTE.warmBirch],
          [1, PALETTE.goldenAmber],
        ],
        PALETTE.warmBirch
      );
      drawPoly(
        ctx,
        [
          [16 * s, 6 * s],
          [41.5 * s, 16.5 * s],
          [27 * s, 23 * s],
          [2 * s, 12.6 * s],
        ],
        palletGrad,
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [2 * s, 12.6 * s],
          [27 * s, 23 * s],
          [27 * s, 25.2 * s],
          [2 * s, 14.8 * s],
        ],
        PALETTE.goldenAmber,
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [27 * s, 23 * s],
          [41.5 * s, 16.5 * s],
          [41.5 * s, 18.7 * s],
          [27 * s, 25.2 * s],
        ],
        PALETTE.classicOak,
        outline,
        1.1 * s
      );

      // Thick Plush Cream Mattress (Top + 3D Side/Front Bevels)
      drawPoly(
        ctx,
        [
          [16.2 * s, 4.6 * s],
          [40.2 * s, 14.2 * s],
          [26.5 * s, 20.4 * s],
          [3.5 * s, 10.8 * s],
        ],
        '#fffbeb',
        outline,
        1.15 * s
      );

      // Two Plump White Pillows with Seam Stitching Against Headboard
      drawPoly(
        ctx,
        [
          [9.8 * s, 8.8 * s],
          [15.4 * s, 11.2 * s],
          [11.4 * s, 13.2 * s],
          [5.8 * s, 10.8 * s],
        ],
        '#ffffff',
        outline,
        1.05 * s
      );
      drawPoly(
        ctx,
        [
          [15.6 * s, 6.2 * s],
          [21.2 * s, 8.6 * s],
          [17.2 * s, 10.6 * s],
          [11.6 * s, 8.2 * s],
        ],
        '#f8fafc',
        outline,
        1.05 * s
      );

      // Cozy Warm Amber-Terracotta Quilted Duvet with Cream Fold-Over Collar
      const blanketGrad = createLinear(
        ctx,
        12 * s,
        8 * s,
        40 * s,
        23 * s,
        [
          [0, '#fb923c'],
          [0.55, '#ea580c'],
          [1, '#9a3412'],
        ],
        PALETTE.sunsetCoral
      );
      drawPoly(
        ctx,
        [
          [24.2 * s, 7.8 * s],
          [40.2 * s, 14.2 * s],
          [26.5 * s, 20.4 * s],
          [11.8 * s, 14.2 * s],
        ],
        blanketGrad,
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [11.8 * s, 14.2 * s],
          [26.5 * s, 20.4 * s],
          [26.5 * s, 23.0 * s],
          [11.8 * s, 16.8 * s],
        ],
        '#c2410c',
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [26.5 * s, 20.4 * s],
          [40.2 * s, 14.2 * s],
          [40.2 * s, 16.8 * s],
          [26.5 * s, 23.0 * s],
        ],
        '#7c2d12',
        outline,
        1.1 * s
      );

      // Folded Cream Turn-Down Sheet Collar
      drawPoly(
        ctx,
        [
          [21.8 * s, 6.8 * s],
          [24.8 * s, 8.0 * s],
          [12.4 * s, 14.5 * s],
          [9.5 * s, 13.2 * s],
        ],
        '#fef3c7',
        outline,
        1.0 * s
      );
    });
  }

  public static generateBedT2(): HTMLCanvasElement | null {
    // 2. bed_t2: Giường Gỗ Sồi Ấm Cúng / Wooden Bed Frame (48x34 Logical -> 192x136 HD)
    // True 2.5D isometric warm oak bed frame with walnut paneled headboard, plush mattress,
    // coral-sunset duvet folded back over a cream sheet, two plump pillows, and attached nightstand with glowing brass lamp.
    return this.createSuperSampledCanvas(48, 34, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Isometric Shadow
      drawPoly(
        ctx,
        [
          [16 * s, 9.5 * s],
          [46 * s, 22.8 * s],
          [28 * s, 33 * s],
          [1.5 * s, 20 * s],
        ],
        'rgba(15, 23, 42, 0.45)'
      );

      // Attached Bedside Nightstand & Glowing Warm Brass Lamp (Back-Right beside Headboard)
      const lampHalo = createRadial(
        ctx,
        24.5 * s,
        5 * s,
        0.5 * s,
        24.5 * s,
        5 * s,
        5.5 * s,
        [
          [0, 'rgba(254, 240, 138, 0.92)'],
          [0.6, 'rgba(245, 158, 11, 0.42)'],
          [1, 'rgba(245, 158, 11, 0)'],
        ],
        'rgba(254, 240, 138, 0.45)'
      );
      ctx.fillStyle = lampHalo;
      ctx.beginPath();
      ctx.arc(24.5 * s, 5 * s, 5.5 * s, 0, Math.PI * 2);
      ctx.fill();

      drawPoly(
        ctx,
        [
          [21 * s, 7.6 * s],
          [28 * s, 10.8 * s],
          [24.5 * s, 12.6 * s],
          [17.5 * s, 9.4 * s],
        ],
        '#d97706',
        outline,
        1 * s
      );
      drawPoly(
        ctx,
        [
          [24.5 * s, 12.6 * s],
          [28 * s, 10.8 * s],
          [28 * s, 15.2 * s],
          [24.5 * s, 17 * s],
        ],
        '#78350f',
        outline,
        1 * s
      );
      drawLine(ctx, 24.5 * s, 6.2 * s, 24.5 * s, 9.4 * s, '#fbbf24', 1.4 * s);
      drawPoly(
        ctx,
        [
          [21.8 * s, 6.2 * s],
          [23 * s, 3 * s],
          [26 * s, 3 * s],
          [27.2 * s, 6.2 * s],
        ],
        '#fef08a',
        outline,
        1.1 * s
      );

      // 3D Isometric Extruded Walnut Paneled Headboard along Back-Left Head Edge
      drawPoly(
        ctx,
        [
          [2.2 * s, 9.4 * s],
          [16.5 * s, 2.2 * s],
          [18.5 * s, 3.2 * s],
          [4.2 * s, 10.4 * s],
        ],
        '#b45309',
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [2.2 * s, 9.4 * s],
          [4.2 * s, 10.4 * s],
          [4.2 * s, 17.5 * s],
          [2.2 * s, 16.5 * s],
        ],
        '#451a03',
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [4.2 * s, 10.4 * s],
          [18.5 * s, 3.2 * s],
          [18.5 * s, 10.2 * s],
          [4.2 * s, 17.5 * s],
        ],
        PALETTE.warmWalnut,
        outline,
        1.25 * s
      );
      // Recessed Twin Walnut Headboard Panels
      drawPoly(
        ctx,
        [
          [5.6 * s, 11 * s],
          [10.8 * s, 8.4 * s],
          [10.8 * s, 12.8 * s],
          [5.6 * s, 15.4 * s],
        ],
        PALETTE.classicOak,
        PALETTE.darkEspresso,
        0.9 * s
      );
      drawPoly(
        ctx,
        [
          [12 * s, 7.8 * s],
          [17.2 * s, 5.2 * s],
          [17.2 * s, 9.6 * s],
          [12 * s, 12.2 * s],
        ],
        PALETTE.classicOak,
        PALETTE.darkEspresso,
        0.9 * s
      );

      // Solid Warm Oak Bed Frame & Corner Legs
      const oakGrad = createLinear(
        ctx,
        3 * s,
        15 * s,
        44 * s,
        32 * s,
        [
          [0, '#f59e0b'],
          [0.55, '#b45309'],
          [1, '#78350f'],
        ],
        PALETTE.classicOak
      );
      drawPoly(
        ctx,
        [
          [3.5 * s, 16.5 * s],
          [28 * s, 27.5 * s],
          [43.8 * s, 19.5 * s],
          [43.8 * s, 23.8 * s],
          [28 * s, 31.8 * s],
          [3.5 * s, 20.8 * s],
        ],
        oakGrad,
        outline,
        1.25 * s
      );
      drawLine(ctx, 4.2 * s, 20.8 * s, 4.2 * s, 23.2 * s, '#451a03', 2 * s);
      drawLine(ctx, 28 * s, 31.5 * s, 28 * s, 33.4 * s, '#451a03', 2 * s);
      drawLine(ctx, 43.2 * s, 23.5 * s, 43.2 * s, 25.5 * s, '#451a03', 2 * s);

      // Plush Cream Fitted Mattress
      drawPoly(
        ctx,
        [
          [18.2 * s, 9.2 * s],
          [42.8 * s, 19.5 * s],
          [28 * s, 27 * s],
          [4.2 * s, 16.2 * s],
        ],
        '#fffbeb',
        outline,
        1.15 * s
      );

      // Two Plump Isometric Pillows Side-by-Side Along Headboard
      drawPoly(
        ctx,
        [
          [11.2 * s, 11.8 * s],
          [17.2 * s, 14.5 * s],
          [12.8 * s, 16.8 * s],
          [6.8 * s, 14.1 * s],
        ],
        '#ffffff',
        outline,
        1.05 * s
      );
      drawPoly(
        ctx,
        [
          [17.2 * s, 8.8 * s],
          [23.2 * s, 11.5 * s],
          [18.8 * s, 13.8 * s],
          [12.8 * s, 11.1 * s],
        ],
        '#f8fafc',
        outline,
        1.05 * s
      );

      // Coral-Sunset Quilted Duvet with Folded Cream Turn-Down Collar
      const duvetGrad = createLinear(
        ctx,
        10 * s,
        13 * s,
        43 * s,
        30 * s,
        [
          [0, '#fb7185'],
          [0.55, '#e11d48'],
          [1, '#9f1239'],
        ],
        PALETTE.sunsetCoral
      );
      drawPoly(
        ctx,
        [
          [27.8 * s, 13.2 * s],
          [42.8 * s, 19.5 * s],
          [28 * s, 27 * s],
          [13.2 * s, 20.2 * s],
        ],
        duvetGrad,
        outline,
        1.2 * s
      );
      drawPoly(
        ctx,
        [
          [13.2 * s, 20.2 * s],
          [28 * s, 27 * s],
          [28 * s, 30 * s],
          [13.2 * s, 23.2 * s],
        ],
        '#be123c',
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [28 * s, 27 * s],
          [42.8 * s, 19.5 * s],
          [42.8 * s, 22.5 * s],
          [28 * s, 30 * s],
        ],
        '#881337',
        outline,
        1.15 * s
      );

      // Cream Fold-Back Duvet Collar Spanning Parallel to Headboard
      drawPoly(
        ctx,
        [
          [25.2 * s, 12.1 * s],
          [28.5 * s, 13.5 * s],
          [14 * s, 20.6 * s],
          [10.8 * s, 19.1 * s],
        ],
        '#fef3c7',
        outline,
        1.05 * s
      );
      drawLine(ctx, 16 * s, 20 * s, 27.5 * s, 25.2 * s, '#fda4af', 1 * s);
    });
  }

  public static generateBedT3(): HTMLCanvasElement | null {
    // Master Penthouse King Bed (56x42 Logical -> 224x168 HD)
    return this.createSuperSampledCanvas(56, 42, 4, (ctx, _w, _h, s) => {
      // Tufted Royal Plum Velvet Headboard
      ctx.fillStyle = HD_PALETTE.velvetPlum;
      ctx.beginPath();
      ctx.roundRect(4 * s, 2 * s, 48 * s, 16 * s, [4 * s]);
      ctx.fill();

      ctx.fillStyle = HD_PALETTE.velvetPlumLight;
      ctx.fillRect(6 * s, 4 * s, 44 * s, 12 * s);

      // Gold Tufted Buttons
      ctx.fillStyle = HD_PALETTE.moldingGold;
      const buttons = [14, 26, 38];
      for (const bx of buttons) {
        ctx.beginPath();
        ctx.arc(bx * s, 10 * s, 2 * s, 0, Math.PI * 2);
        ctx.fill();
      }

      // Dark Espresso Floating Bed Frame
      ctx.fillStyle = PALETTE.darkEspresso;
      ctx.fillRect(4 * s, 16 * s, 48 * s, 20 * s);

      // Cashmere Midnight Duvet with Silk Runner
      ctx.fillStyle = PALETTE.midnightIndigo;
      ctx.beginPath();
      ctx.roundRect(6 * s, 18 * s, 44 * s, 16 * s, [3 * s]);
      ctx.fill();
      ctx.fillStyle = PALETTE.royalPlum;
      ctx.fillRect(6 * s, 18 * s, 44 * s, 6 * s);

      // Dual High-Thread Satin Pillows
      ctx.fillStyle = PALETTE.frostWhite;
      ctx.beginPath();
      ctx.roundRect(8 * s, 12 * s, 16 * s, 8 * s, [3 * s]);
      ctx.roundRect(32 * s, 12 * s, 16 * s, 8 * s, [3 * s]);
      ctx.fill();

      // Cyber Cyan Underglow LED Strip
      ctx.fillStyle = HD_PALETTE.cyberNeonCyan;
      ctx.fillRect(4 * s, 36 * s, 48 * s, 2.5 * s);
      ctx.fillStyle = HD_PALETTE.cyberGlow;
      ctx.fillRect(2 * s, 37 * s, 52 * s, 4 * s);
    });
  }

  // =========================================================================
  // MULTI-TIER REFRIGERATORS (Tiers 1-3)
  // =========================================================================

  public static generateFridgeT1(): HTMLCanvasElement | null {
    // 3. fridge_t1: Tủ Lạnh Mini Sinh Viên / Mini Cooler (22x32 Logical -> 88x128 HD)
    // True 2.5D isometric retro mint/slate mini-fridge box (showing top face, front-right door face,
    // and left side face), curved chrome handle, colorful souvenir magnet stickers, glowing cyan LED, specular sheen.
    return this.createSuperSampledCanvas(22, 32, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Isometric Shadow
      drawPoly(
        ctx,
        [
          [11 * s, 22.2 * s],
          [20.8 * s, 26.8 * s],
          [11 * s, 31.2 * s],
          [1.2 * s, 26.8 * s],
        ],
        'rgba(15, 23, 42, 0.45)'
      );

      // Rubber Feet
      drawLine(ctx, 4 * s, 25 * s, 4 * s, 26.8 * s, '#0f172a', 1.8 * s);
      drawLine(ctx, 11 * s, 28.8 * s, 11 * s, 30.5 * s, '#0f172a', 1.8 * s);
      drawLine(ctx, 18 * s, 25 * s, 18 * s, 26.8 * s, '#0f172a', 1.8 * s);

      // 2.5D Top Enamel Face
      const topGrad = createLinear(
        ctx,
        2.5 * s,
        5 * s,
        19.5 * s,
        14 * s,
        [
          [0, '#ccfbf1'],
          [1, '#5eead4'],
        ],
        PALETTE.frostWhite
      );
      drawPoly(
        ctx,
        [
          [11 * s, 5.2 * s],
          [19.5 * s, 9.4 * s],
          [11 * s, 13.6 * s],
          [2.5 * s, 9.4 * s],
        ],
        topGrad,
        outline,
        1.2 * s
      );

      // 2.5D Shaded Left Side Cabinet Face
      const sideGrad = createLinear(
        ctx,
        2.5 * s,
        9.4 * s,
        11 * s,
        29.5 * s,
        [
          [0, '#115e59'],
          [1, '#134e4a'],
        ],
        PALETTE.oceanTeal
      );
      drawPoly(
        ctx,
        [
          [2.5 * s, 9.4 * s],
          [11 * s, 13.6 * s],
          [11 * s, 29.5 * s],
          [2.5 * s, 25.2 * s],
        ],
        sideGrad,
        outline,
        1.2 * s
      );

      // 2.5D Front-Right Glossy Enamel Door Face
      const doorGrad = createLinear(
        ctx,
        11 * s,
        9.4 * s,
        19.5 * s,
        29.5 * s,
        [
          [0, '#2dd4bf'],
          [0.55, '#0d9488'],
          [1, '#0f766e'],
        ],
        PALETTE.neonCyan
      );
      drawPoly(
        ctx,
        [
          [11 * s, 13.6 * s],
          [19.5 * s, 9.4 * s],
          [19.5 * s, 25.2 * s],
          [11 * s, 29.5 * s],
        ],
        doorGrad,
        outline,
        1.2 * s
      );

      // Door Gasket Seam & Diagonal Specular Sheen
      drawLine(ctx, 12.1 * s, 13.1 * s, 12.1 * s, 28.8 * s, '#042f2e', 0.9 * s);
      drawPoly(
        ctx,
        [
          [13.4 * s, 13.2 * s],
          [18.2 * s, 10.8 * s],
          [18.2 * s, 14.5 * s],
          [13.4 * s, 16.9 * s],
        ],
        'rgba(255, 255, 255, 0.28)'
      );

      // Curved Chrome Latch Handle with Crisp Outline
      drawLine(ctx, 13 * s, 16.5 * s, 13 * s, 20.5 * s, outline, 2.2 * s);
      drawLine(ctx, 13 * s, 16.5 * s, 13 * s, 20.5 * s, '#ffffff', 1.4 * s);

      // Glowing Cyan Power LED Indicator
      ctx.fillStyle = 'rgba(34, 211, 238, 0.4)';
      ctx.beginPath();
      ctx.arc(17.5 * s, 12.2 * s, 1.5 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#22d3ee';
      ctx.beginPath();
      ctx.arc(17.5 * s, 12.2 * s, 0.85 * s, 0, Math.PI * 2);
      ctx.fill();

      // Colorful Souvenir Magnet Stickers (NY Red Apple & Sunny Postcard)
      ctx.fillStyle = '#f43f5e';
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.7 * s;
      ctx.beginPath();
      ctx.arc(15.2 * s, 18.2 * s, 1.3 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      drawPoly(
        ctx,
        [
          [16 * s, 21.2 * s],
          [18.2 * s, 20.2 * s],
          [18.2 * s, 22.8 * s],
          [16 * s, 23.8 * s],
        ],
        '#facc15',
        outline,
        0.7 * s
      );

      // Stainless Moka Espresso Pot on Top Face (with Lid Knob & Handle)
      drawPoly(
        ctx,
        [
          [9.2 * s, 6.2 * s],
          [12.8 * s, 6.2 * s],
          [12.2 * s, 10 * s],
          [9.8 * s, 10 * s],
        ],
        '#e2e8f0',
        outline,
        0.95 * s
      );
      drawPoly(
        ctx,
        [
          [8.8 * s, 4.8 * s],
          [13.2 * s, 4.8 * s],
          [12.8 * s, 6.2 * s],
          [9.2 * s, 6.2 * s],
        ],
        '#94a3b8',
        outline,
        0.9 * s
      );
      ctx.fillStyle = outline;
      ctx.beginPath();
      ctx.arc(11 * s, 4.1 * s, 0.75 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.05 * s;
      ctx.beginPath();
      ctx.moveTo(12.8 * s, 5.6 * s);
      ctx.quadraticCurveTo(14.5 * s, 6.2 * s, 14.3 * s, 8 * s);
      ctx.lineTo(12.5 * s, 8.6 * s);
      ctx.stroke();
    });
  }

  public static generateFridgeT2(): HTMLCanvasElement | null {
    // 4. fridge_t2: Tủ Lạnh Gia Đình 2 Ngăn / Standard Apartment Fridge (26x44 Logical -> 104x176 HD)
    // True 2.5D isometric tall brushed-stainless double-door apartment refrigerator (top freezer + bottom fridge),
    // 3D vertical metallic handles, chilled water dispenser recess with status LEDs, and crisp side/top bevels.
    return this.createSuperSampledCanvas(26, 44, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Isometric Shadow
      drawPoly(
        ctx,
        [
          [13 * s, 32 * s],
          [24.5 * s, 37.8 * s],
          [13 * s, 43.2 * s],
          [1.5 * s, 37.8 * s],
        ],
        'rgba(15, 23, 42, 0.48)'
      );

      // 2.5D Top Brushed Steel Cap
      const topGrad = createLinear(
        ctx,
        2.5 * s,
        2 * s,
        23.5 * s,
        12 * s,
        [
          [0, '#f8fafc'],
          [1, '#94a3b8'],
        ],
        PALETTE.frostWhite
      );
      drawPoly(
        ctx,
        [
          [13 * s, 2.2 * s],
          [23.5 * s, 7.2 * s],
          [13 * s, 12.2 * s],
          [2.5 * s, 7.2 * s],
        ],
        topGrad,
        outline,
        1.25 * s
      );

      // 2.5D Left Shaded Brushed Steel Cabinet
      const sideGrad = createLinear(
        ctx,
        2.5 * s,
        7.2 * s,
        13 * s,
        41.5 * s,
        [
          [0, '#475569'],
          [1, '#1e293b'],
        ],
        PALETTE.deepSteel
      );
      drawPoly(
        ctx,
        [
          [2.5 * s, 7.2 * s],
          [13 * s, 12.2 * s],
          [13 * s, 41.5 * s],
          [2.5 * s, 36.5 * s],
        ],
        sideGrad,
        outline,
        1.25 * s
      );

      // 2.5D Front-Right Doors (Top Freezer + Bottom Main Fridge)
      const doorGrad = createLinear(
        ctx,
        13 * s,
        7.2 * s,
        23.5 * s,
        41.5 * s,
        [
          [0, '#e2e8f0'],
          [0.5, '#94a3b8'],
          [1, '#475569'],
        ],
        PALETTE.slateGray
      );
      drawPoly(
        ctx,
        [
          [13 * s, 12.2 * s],
          [23.5 * s, 7.2 * s],
          [23.5 * s, 36.5 * s],
          [13 * s, 41.5 * s],
        ],
        doorGrad,
        outline,
        1.25 * s
      );

      // Deep Horizontal Isometric Freezer/Fridge Divider Channel
      drawLine(ctx, 13 * s, 21.8 * s, 23.5 * s, 16.8 * s, '#0f172a', 1.8 * s);
      drawLine(ctx, 13 * s, 22.5 * s, 23.5 * s, 17.5 * s, 'rgba(255, 255, 255, 0.6)', 0.7 * s);

      // Diagonal Brushed Stainless Specular Sheen
      drawPoly(
        ctx,
        [
          [16 * s, 11.2 * s],
          [22 * s, 8.4 * s],
          [22 * s, 12.2 * s],
          [16 * s, 15 * s],
        ],
        'rgba(255, 255, 255, 0.3)'
      );
      drawPoly(
        ctx,
        [
          [16 * s, 24.5 * s],
          [22.5 * s, 21.4 * s],
          [22.5 * s, 26 * s],
          [16 * s, 29.1 * s],
        ],
        'rgba(255, 255, 255, 0.22)'
      );

      // 3D Vertical Tubular Metallic Handles (Upper & Lower)
      drawLine(ctx, 14.8 * s, 13.2 * s, 14.8 * s, 19 * s, outline, 2.2 * s);
      drawLine(ctx, 14.8 * s, 13.2 * s, 14.8 * s, 19 * s, '#f8fafc', 1.4 * s);
      drawLine(ctx, 14.8 * s, 24 * s, 14.8 * s, 35.5 * s, outline, 2.2 * s);
      drawLine(ctx, 14.8 * s, 24 * s, 14.8 * s, 35.5 * s, '#f8fafc', 1.4 * s);

      // Chilled Water & Ice Dispenser Recess on Lower Door with Status LEDs & Nozzle
      drawPoly(
        ctx,
        [
          [17.5 * s, 22.8 * s],
          [21.8 * s, 20.8 * s],
          [21.8 * s, 27.8 * s],
          [17.5 * s, 29.8 * s],
        ],
        '#0f172a',
        '#334155',
        0.9 * s
      );
      drawLine(ctx, 19.6 * s, 23.2 * s, 19.6 * s, 25.8 * s, '#94a3b8', 0.9 * s);
      ctx.fillStyle = '#38bdf8';
      ctx.beginPath();
      ctx.arc(19 * s, 23 * s, 0.7 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#4ade80';
      ctx.beginPath();
      ctx.arc(20.5 * s, 22.3 * s, 0.7 * s, 0, Math.PI * 2);
      ctx.fill();

      // Souvenir Magnets on Upper Freezer Door
      ctx.fillStyle = '#f43f5e';
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.7 * s;
      ctx.beginPath();
      ctx.arc(18.2 * s, 13.5 * s, 1.3 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      drawPoly(
        ctx,
        [
          [19.8 * s, 12.2 * s],
          [22 * s, 11.2 * s],
          [22 * s, 13.8 * s],
          [19.8 * s, 14.8 * s],
        ],
        '#facc15',
        outline,
        0.7 * s
      );
    });
  }

  public static generateFridgeT3(): HTMLCanvasElement | null {
    // Smart French-Door Chef Refrigerator (34x48 Logical -> 136x192 HD)
    return this.createSuperSampledCanvas(34, 48, 4, (ctx, _w, _h, s) => {
      // Obsidian Stainless Steel Body
      ctx.fillStyle = PALETTE.deepSteel;
      ctx.beginPath();
      ctx.roundRect(2 * s, 2 * s, 30 * s, 44 * s, [4 * s]);
      ctx.fill();

      ctx.fillStyle = PALETTE.slateGray;
      ctx.fillRect(4 * s, 4 * s, 26 * s, 24 * s); // Upper French doors
      ctx.fillRect(4 * s, 30 * s, 26 * s, 14 * s); // Lower pullout drawer

      // Center French Door Seam
      ctx.fillStyle = PALETTE.deepInk;
      ctx.fillRect(16 * s, 4 * s, 2 * s, 24 * s);

      // Smart Interactive Digital Touchscreen on Right Door
      ctx.fillStyle = HD_PALETTE.blackSteel;
      ctx.fillRect(19 * s, 7 * s, 10 * s, 16 * s);

      ctx.fillStyle = HD_PALETTE.cyberNeonCyan;
      ctx.fillRect(20 * s, 8 * s, 8 * s, 14 * s);

      // App widgets on screen
      ctx.fillStyle = PALETTE.deepInk;
      ctx.fillRect(22 * s, 10 * s, 4 * s, 2 * s);
      ctx.fillRect(22 * s, 14 * s, 4 * s, 4 * s);

      // Vertical Tubular Handles
      ctx.fillStyle = PALETTE.paleSilver;
      ctx.fillRect(14 * s, 10 * s, 1.8 * s, 12 * s);
      ctx.fillRect(17.2 * s, 10 * s, 1.8 * s, 12 * s);
    });
  }

  // =========================================================================
  // MULTI-TIER PC DESKS (Tiers 1-3)
  // =========================================================================

  public static generateDeskT1(): HTMLCanvasElement | null {
    // 5. desk_t1: Bàn Gấp Laptop Cũ / Old Folding Table (42x30 Logical -> 168x120 HD)
    // True 2.5D isometric warm pine tabletop on X-frame matte steel legs, featuring an open glowing
    // laptop with code lines on screen, a ceramic coffee mug with rising steam, spiral notebook, wireless mouse.
    return this.createSuperSampledCanvas(42, 30, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Shadow
      drawPoly(
        ctx,
        [
          [15 * s, 14.5 * s],
          [40.5 * s, 25 * s],
          [26.5 * s, 29.8 * s],
          [2 * s, 19.5 * s],
        ],
        'rgba(15, 23, 42, 0.36)'
      );

      // Matte Charcoal X-Frame Folding Steel Legs
      drawLine(ctx, 5.5 * s, 13.5 * s, 12 * s, 24.5 * s, '#334155', 1.8 * s);
      drawLine(ctx, 12 * s, 14.5 * s, 5 * s, 23.5 * s, '#334155', 1.8 * s);
      drawLine(ctx, 27.5 * s, 20.5 * s, 35.5 * s, 28.8 * s, '#1e293b', 1.9 * s);
      drawLine(ctx, 36.5 * s, 18.5 * s, 27 * s, 29.2 * s, '#1e293b', 1.9 * s);
      drawLine(ctx, 8.5 * s, 19.5 * s, 31.5 * s, 26.5 * s, '#475569', 1.1 * s);

      // 2.5D Isometric Warm Pine Tabletop (Top + Left Bevel + Front-Right Bevel)
      const pineGrad = createLinear(
        ctx,
        2 * s,
        7 * s,
        40 * s,
        23 * s,
        [
          [0, '#fde68a'],
          [0.55, '#f59e0b'],
          [1, '#d97706'],
        ],
        PALETTE.warmBirch
      );
      drawPoly(
        ctx,
        [
          [15 * s, 7.2 * s],
          [40 * s, 17.8 * s],
          [26.5 * s, 24.2 * s],
          [1.8 * s, 13.6 * s],
        ],
        pineGrad,
        outline,
        1.2 * s
      );
      drawPoly(
        ctx,
        [
          [1.8 * s, 13.6 * s],
          [26.5 * s, 24.2 * s],
          [26.5 * s, 26.4 * s],
          [1.8 * s, 15.8 * s],
        ],
        '#b45309',
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [26.5 * s, 24.2 * s],
          [40 * s, 17.8 * s],
          [40 * s, 20 * s],
          [26.5 * s, 26.4 * s],
        ],
        '#78350f',
        outline,
        1.1 * s
      );

      // Spiral Notebook & Wireless Mouse
      drawPoly(
        ctx,
        [
          [8 * s, 12.2 * s],
          [13 * s, 14.4 * s],
          [10.2 * s, 16 * s],
          [5.2 * s, 13.8 * s],
        ],
        '#fef08a',
        outline,
        0.9 * s
      );
      drawLine(ctx, 7.6 * s, 12.8 * s, 11.6 * s, 14.5 * s, '#ca8a04', 0.7 * s);
      ctx.fillStyle = '#f8fafc';
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.85 * s;
      ctx.beginPath();
      ctx.ellipse(27.5 * s, 18.6 * s, 1.8 * s, 1.1 * s, 0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Open 2.5D Laptop (Keyboard Deck + Glowing Code Screen)
      drawPoly(
        ctx,
        [
          [14 * s, 12.6 * s],
          [23.8 * s, 16.8 * s],
          [19.6 * s, 19 * s],
          [9.8 * s, 14.8 * s],
        ],
        '#475569',
        outline,
        1.05 * s
      );
      drawPoly(
        ctx,
        [
          [15.4 * s, 3.8 * s],
          [25.2 * s, 8 * s],
          [23.8 * s, 16.8 * s],
          [14 * s, 12.6 * s],
        ],
        '#0f172a',
        outline,
        1.15 * s
      );
      const screenGrad = createLinear(
        ctx,
        15 * s,
        5 * s,
        24 * s,
        16 * s,
        [
          [0, '#38bdf8'],
          [1, '#0369a1'],
        ],
        PALETTE.neonCyan
      );
      drawPoly(
        ctx,
        [
          [16.4 * s, 5.2 * s],
          [24 * s, 8.5 * s],
          [22.9 * s, 15.4 * s],
          [15.3 * s, 12.1 * s],
        ],
        screenGrad
      );
      // Code Syntax Lines & Cyan Screen Glow Halo
      const cyanGlow = createRadial(
        ctx,
        19.5 * s,
        10.2 * s,
        1 * s,
        19.5 * s,
        10.2 * s,
        8.5 * s,
        [
          [0, 'rgba(56, 189, 248, 0.35)'],
          [1, 'rgba(56, 189, 248, 0)'],
        ],
        'rgba(56, 189, 248, 0.18)'
      );
      ctx.fillStyle = cyanGlow;
      ctx.beginPath();
      ctx.arc(19.5 * s, 10.2 * s, 8.5 * s, 0, Math.PI * 2);
      ctx.fill();

      drawLine(ctx, 17.2 * s, 7.2 * s, 21 * s, 8.9 * s, '#4ade80', 1.05 * s);
      drawLine(ctx, 17 * s, 9.1 * s, 22.2 * s, 11.4 * s, '#facc15', 1.05 * s);
      drawLine(ctx, 16.8 * s, 11 * s, 20.4 * s, 12.6 * s, '#ffffff', 0.95 * s);

      // Ceramic Coffee Mug with Dark Espresso Surface, Curved Handle & Rising Steam
      drawPoly(
        ctx,
        [
          [30 * s, 13.5 * s],
          [34.2 * s, 13.5 * s],
          [34 * s, 17.8 * s],
          [30.2 * s, 17.8 * s],
        ],
        '#f97316',
        outline,
        1.05 * s
      );
      ctx.fillStyle = '#451a03';
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.85 * s;
      ctx.beginPath();
      ctx.ellipse(32.1 * s, 13.5 * s, 2.1 * s, 0.85 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.15 * s;
      ctx.beginPath();
      ctx.moveTo(34.2 * s, 14.3 * s);
      ctx.quadraticCurveTo(36 * s, 14.3 * s, 36 * s, 15.6 * s);
      ctx.quadraticCurveTo(36 * s, 16.9 * s, 34.1 * s, 16.9 * s);
      ctx.stroke();
      drawLine(ctx, 31.2 * s, 11.6 * s, 32 * s, 9.2 * s, 'rgba(255, 255, 255, 0.85)', 0.9 * s);
      drawLine(ctx, 33 * s, 12.2 * s, 33.8 * s, 9.8 * s, 'rgba(255, 255, 255, 0.7)', 0.9 * s);

      // Ergonomic Swivel Chair Tucked at Front-Left of Desk
      // 5-star caster base & gas cylinder
      drawLine(ctx, 15.5 * s, 24.5 * s, 15.5 * s, 28.5 * s, '#1e293b', 1.8 * s);
      drawLine(ctx, 11.5 * s, 28.8 * s, 19.5 * s, 28.2 * s, '#334155', 1.5 * s);
      drawLine(ctx, 13.2 * s, 27.2 * s, 17.8 * s, 29.5 * s, '#334155', 1.5 * s);
      // Cushioned swivel seat pan
      drawPoly(
        ctx,
        [
          [11.5 * s, 21.8 * s],
          [19.2 * s, 23.2 * s],
          [16.8 * s, 25.8 * s],
          [9.5 * s, 24.2 * s],
        ],
        '#1e293b',
        outline,
        1.05 * s
      );
      // Curved mesh backrest with cyan gaming accent trim
      drawPoly(
        ctx,
        [
          [10.2 * s, 16.2 * s],
          [15.8 * s, 17.6 * s],
          [14.8 * s, 24.2 * s],
          [9.5 * s, 22.8 * s],
        ],
        '#0f172a',
        '#38bdf8',
        1.05 * s
      );
    });
  }

  public static generateDeskT2(): HTMLCanvasElement | null {
    // 6. desk_t2: Bàn Công Thái Học 2 Màn Hình / Ergonomic Standing Tech Desk (48x36 Logical -> 192x144 HD)
    // True 2.5D isometric walnut ergonomic standing desk with motorized T-legs, dual curved widescreen
    // monitors on an articulating arm displaying glowing charts/code, mechanical keyboard with wrist rest, desk mat, studio lamp.
    return this.createSuperSampledCanvas(48, 36, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Shadow
      drawPoly(
        ctx,
        [
          [16 * s, 18.5 * s],
          [45.5 * s, 29.5 * s],
          [29.5 * s, 35.5 * s],
          [2 * s, 24.5 * s],
        ],
        'rgba(15, 23, 42, 0.44)'
      );

      // Motorized Standing Desk T-Legs & Stabilizer Feet
      drawLine(ctx, 8.5 * s, 18 * s, 8.5 * s, 28.5 * s, '#1e293b', 2.4 * s);
      drawLine(ctx, 5.5 * s, 29.8 * s, 12.5 * s, 26.8 * s, '#1e293b', 2.4 * s);
      drawLine(ctx, 34 * s, 25.5 * s, 34 * s, 33.5 * s, '#0f172a', 2.4 * s);
      drawLine(ctx, 30 * s, 34.8 * s, 38 * s, 31.8 * s, '#0f172a', 2.4 * s);
      drawLine(ctx, 8.5 * s, 21 * s, 8.5 * s, 25 * s, '#38bdf8', 1 * s);
      drawLine(ctx, 34 * s, 27.5 * s, 34 * s, 31 * s, '#38bdf8', 1 * s);

      // 2.5D Ergonomic Walnut Tabletop
      const walnutGrad = createLinear(
        ctx,
        2 * s,
        11 * s,
        45 * s,
        28 * s,
        [
          [0, '#b45309'],
          [0.55, '#78350f'],
          [1, '#451a03'],
        ],
        PALETTE.warmWalnut
      );
      drawPoly(
        ctx,
        [
          [16.8 * s, 11 * s],
          [45.5 * s, 21.8 * s],
          [29.5 * s, 29.2 * s],
          [2 * s, 18.4 * s],
        ],
        walnutGrad,
        outline,
        1.25 * s
      );
      drawPoly(
        ctx,
        [
          [2 * s, 18.4 * s],
          [29.5 * s, 29.2 * s],
          [29.5 * s, 31.4 * s],
          [2 * s, 20.6 * s],
        ],
        '#451a03',
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [29.5 * s, 29.2 * s],
          [45.5 * s, 21.8 * s],
          [45.5 * s, 24 * s],
          [29.5 * s, 31.4 * s],
        ],
        '#24171e',
        outline,
        1.15 * s
      );

      // Extended Dark Felt Desk Mat with Cyan Trim
      drawPoly(
        ctx,
        [
          [15.5 * s, 13.6 * s],
          [39.5 * s, 22.8 * s],
          [28.5 * s, 27.8 * s],
          [5.5 * s, 18.6 * s],
        ],
        '#1e293b',
        '#38bdf8',
        0.85 * s
      );

      // Mechanical Keyboard with RGB & Plush Wrist Rest + Wireless Mouse
      drawPoly(
        ctx,
        [
          [15 * s, 17.2 * s],
          [25.5 * s, 21.2 * s],
          [22.8 * s, 22.8 * s],
          [12.2 * s, 18.8 * s],
        ],
        '#334155',
        '#f8fafc',
        0.75 * s
      );
      drawLine(ctx, 15.5 * s, 18.4 * s, 23 * s, 21.4 * s, '#38bdf8', 0.95 * s);
      // Attached Dark Cushioned Wrist Rest along front-left edge of keyboard
      drawPoly(
        ctx,
        [
          [12.2 * s, 18.8 * s],
          [22.8 * s, 22.8 * s],
          [21.4 * s, 23.8 * s],
          [10.8 * s, 19.8 * s],
        ],
        '#0f172a',
        '#475569',
        0.7 * s
      );
      ctx.fillStyle = '#94a3b8';
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.8 * s;
      ctx.beginPath();
      ctx.ellipse(29.5 * s, 23.4 * s, 1.7 * s, 1 * s, 0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Articulating Dual-Monitor Mount Post + Studio Lamp Goose-Neck Arm
      drawLine(ctx, 25 * s, 16.8 * s, 25 * s, 5.5 * s, '#475569', 2.2 * s);
      drawLine(ctx, 25 * s, 5.5 * s, 26.2 * s, 5.8 * s, '#334155', 1.6 * s);

      // Warm Studio Lamp Light Cone
      drawPoly(
        ctx,
        [
          [18 * s, 2.8 * s],
          [34 * s, 8.5 * s],
          [36 * s, 13.5 * s],
          [16 * s, 7.5 * s],
        ],
        'rgba(254, 240, 138, 0.16)'
      );

      // Left Widescreen Monitor (Code IDE)
      const mon1Grad = createLinear(
        ctx,
        10 * s,
        4 * s,
        24.5 * s,
        16 * s,
        [
          [0, '#0284c7'],
          [1, '#0f172a'],
        ],
        PALETTE.oceanTeal
      );
      drawPoly(
        ctx,
        [
          [10 * s, 3.8 * s],
          [24.5 * s, 8.5 * s],
          [24.5 * s, 16.5 * s],
          [10 * s, 11.8 * s],
        ],
        mon1Grad,
        '#38bdf8',
        1.15 * s
      );
      drawLine(ctx, 12.2 * s, 6.8 * s, 19.5 * s, 9.2 * s, '#4ade80', 1.15 * s);
      drawLine(ctx, 12.2 * s, 8.9 * s, 21.5 * s, 12 * s, '#38bdf8', 1.15 * s);
      drawLine(ctx, 12.2 * s, 11 * s, 17.5 * s, 12.8 * s, '#facc15', 1.15 * s);

      // Right Curved Monitor (Analytics & Charts)
      const mon2Grad = createLinear(
        ctx,
        24.5 * s,
        8.5 * s,
        40.5 * s,
        22.5 * s,
        [
          [0, '#312e81'],
          [1, '#0f172a'],
        ],
        PALETTE.midnightIndigo
      );
      drawPoly(
        ctx,
        [
          [24.5 * s, 8.5 * s],
          [40.5 * s, 14.5 * s],
          [40.5 * s, 22.5 * s],
          [24.5 * s, 16.5 * s],
        ],
        mon2Grad,
        '#38bdf8',
        1.15 * s
      );
      drawLine(ctx, 27 * s, 15.8 * s, 30.5 * s, 14.5 * s, '#34d399', 1.3 * s);
      drawLine(ctx, 30.5 * s, 14.5 * s, 34 * s, 17 * s, '#34d399', 1.3 * s);
      drawLine(ctx, 34 * s, 17 * s, 38 * s, 14.8 * s, '#34d399', 1.3 * s);
      drawLine(ctx, 27.5 * s, 11.8 * s, 34.5 * s, 14.4 * s, '#f472b6', 1.15 * s);

      // Steaming Coffee Mug on Desk Corner
      drawPoly(
        ctx,
        [
          [34.5 * s, 21.5 * s],
          [37.8 * s, 21.5 * s],
          [37.6 * s, 24.8 * s],
          [34.7 * s, 24.8 * s],
        ],
        '#f97316',
        outline,
        0.95 * s
      );
      drawLine(ctx, 35.8 * s, 20.2 * s, 36.4 * s, 18.2 * s, 'rgba(255, 255, 255, 0.8)', 0.8 * s);

      // Ergonomic Gaming Swivel Chair Tucked at Front-Left
      drawLine(ctx, 17.5 * s, 29.5 * s, 17.5 * s, 34 * s, '#1e293b', 1.9 * s);
      drawLine(ctx, 13.5 * s, 34.2 * s, 21.5 * s, 33.8 * s, '#334155', 1.6 * s);
      drawPoly(
        ctx,
        [
          [13 * s, 26.8 * s],
          [21.5 * s, 28.4 * s],
          [19 * s, 31.2 * s],
          [11 * s, 29.4 * s],
        ],
        '#1e293b',
        outline,
        1.05 * s
      );
      drawPoly(
        ctx,
        [
          [11.8 * s, 20.4 * s],
          [17.8 * s, 21.8 * s],
          [16.8 * s, 29.2 * s],
          [11 * s, 27.8 * s],
        ],
        '#0f172a',
        '#38bdf8',
        1.05 * s
      );

      // Studio Task Light Bar Above Monitors (Housing + Warm LED Strip)
      drawLine(ctx, 17.8 * s, 2.7 * s, 34.2 * s, 8.6 * s, outline, 2.6 * s);
      drawLine(ctx, 18 * s, 2.8 * s, 34 * s, 8.5 * s, '#fef08a', 1.6 * s);
    });
  }

  public static generateDeskT3(): HTMLCanvasElement | null {
    // Cyber Battlestation (56x44 Logical -> 224x176 HD)
    return this.createSuperSampledCanvas(56, 44, 4, (ctx, _w, _h, s) => {
      // Sleek Matte Black Surface with Neon Edge Trim
      ctx.fillStyle = PALETTE.deepSteel;
      ctx.beginPath();
      ctx.roundRect(4 * s, 18 * s, 48 * s, 8 * s, [2 * s]);
      ctx.fill();

      ctx.fillStyle = HD_PALETTE.cyberNeonCyan;
      ctx.fillRect(4 * s, 24 * s, 48 * s, 2 * s);

      // Heavy Iron Legs
      ctx.fillStyle = PALETTE.deepInk;
      ctx.fillRect(6 * s, 26 * s, 4 * s, 16 * s);
      ctx.fillRect(46 * s, 26 * s, 4 * s, 16 * s);

      // Curved Ultrawide Triple Monitor Array
      ctx.fillStyle = PALETTE.deepInk;
      ctx.fillRect(6 * s, 2 * s, 44 * s, 16 * s);
      ctx.fillStyle = PALETTE.oceanTeal;
      ctx.fillRect(8 * s, 4 * s, 40 * s, 12 * s);

      // Code & Trading Candlestick Charts
      ctx.fillStyle = HD_PALETTE.cyberNeonCyan;
      ctx.fillRect(12 * s, 6 * s, 10 * s, 2 * s);
      ctx.fillRect(12 * s, 9 * s, 8 * s, 2 * s);

      ctx.fillStyle = PALETTE.sunsetCoral;
      ctx.fillRect(28 * s, 6 * s, 16 * s, 8 * s);

      // RGB Water-Cooled PC Tower
      ctx.fillStyle = PALETTE.deepInk;
      ctx.fillRect(44 * s, 10 * s, 8 * s, 14 * s);
      ctx.fillStyle = HD_PALETTE.cyberNeonCyan;
      ctx.fillRect(46 * s, 12 * s, 4 * s, 4 * s);
      ctx.fillStyle = HD_PALETTE.rgbMagenta;
      ctx.fillRect(46 * s, 18 * s, 4 * s, 4 * s);
    });
  }

  // =========================================================================
  // MULTI-TIER SOFAS (Tiers 1-3)
  // =========================================================================

  public static generateSofaT1(): HTMLCanvasElement | null {
    // 7. sofa_t1: Ghế Lười Hạt Xốp / Casual Beanbag (30x26 Logical -> 120x104 HD)
    // True 2.5D isometric plush teardrop corduroy beanbag lounge in warm terracotta/coral with
    // matching round ottoman pouf, a cozy accent cushion, and a woven floor mat underneath.
    return this.createSuperSampledCanvas(30, 26, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Shadow
      drawPoly(
        ctx,
        [
          [12.5 * s, 9.5 * s],
          [29 * s, 17.5 * s],
          [17.5 * s, 25.2 * s],
          [1 * s, 17.2 * s],
        ],
        'rgba(15, 23, 42, 0.38)'
      );

      // Woven Bohemian Floor Mat Underneath (2:1 Isometric Diamond)
      const matGrad = createLinear(
        ctx,
        2 * s,
        10 * s,
        28 * s,
        24 * s,
        [
          [0, '#fef3c7'],
          [1, '#fde68a'],
        ],
        PALETTE.paleCream
      );
      drawPoly(
        ctx,
        [
          [12.5 * s, 9.8 * s],
          [28.2 * s, 17.4 * s],
          [17.5 * s, 24.4 * s],
          [1.8 * s, 16.8 * s],
        ],
        matGrad,
        outline,
        1.1 * s
      );
      // Inner Woven Mat Ring
      ctx.save();
      ctx.setLineDash([1.5 * s, 1 * s]);
      drawPoly(
        ctx,
        [
          [12.5 * s, 11.4 * s],
          [25.6 * s, 17.6 * s],
          [17.5 * s, 22.8 * s],
          [4.4 * s, 16.6 * s],
        ],
        undefined,
        '#d97706',
        0.75 * s
      );
      ctx.restore();

      // Plump Teardrop Corduroy Beanbag Lounge (Single Smooth Sculpted Contour)
      const bagGrad = createRadial(
        ctx,
        11 * s,
        10 * s,
        1 * s,
        12 * s,
        13 * s,
        10 * s,
        [
          [0, '#fdba74'],
          [0.5, '#f97316'],
          [0.85, '#c2410c'],
          [1, '#7c2d12'],
        ],
        PALETTE.sunsetCoral
      );
      ctx.beginPath();
      ctx.moveTo(4 * s, 14.5 * s);
      ctx.bezierCurveTo(4 * s, 9 * s, 8.5 * s, 4.2 * s, 12 * s, 4.2 * s);
      ctx.bezierCurveTo(15.5 * s, 4.2 * s, 20.2 * s, 9.5 * s, 20.2 * s, 14.2 * s);
      ctx.bezierCurveTo(20.2 * s, 18.2 * s, 16.5 * s, 20.4 * s, 12 * s, 20.4 * s);
      ctx.bezierCurveTo(7.5 * s, 20.4 * s, 4 * s, 18 * s, 4 * s, 14.5 * s);
      ctx.closePath();
      ctx.fillStyle = bagGrad;
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.25 * s;
      ctx.stroke();

      // Sunken Plush Seat Bowl
      ctx.beginPath();
      ctx.ellipse(12.5 * s, 13.8 * s, 4.8 * s, 2.8 * s, 0.2, 0, Math.PI * 2);
      ctx.fillStyle = '#fed7aa';
      ctx.fill();
      ctx.strokeStyle = '#c2410c';
      ctx.lineWidth = 0.9 * s;
      ctx.stroke();

      // Corduroy Ribbing Seams & Top Carry Loop
      ctx.strokeStyle = '#9a3412';
      ctx.lineWidth = 0.85 * s;
      ctx.beginPath();
      ctx.moveTo(12 * s, 4.3 * s);
      ctx.quadraticCurveTo(9.5 * s, 7.8 * s, 8.2 * s, 11.2 * s);
      ctx.moveTo(12 * s, 4.3 * s);
      ctx.quadraticCurveTo(14.5 * s, 7.8 * s, 15.8 * s, 11.4 * s);
      ctx.moveTo(7 * s, 17 * s);
      ctx.quadraticCurveTo(12 * s, 19.2 * s, 17.5 * s, 17 * s);
      ctx.stroke();

      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.1 * s;
      ctx.beginPath();
      ctx.moveTo(10.8 * s, 4.2 * s);
      ctx.quadraticCurveTo(12 * s, 2.3 * s, 13.2 * s, 4.2 * s);
      ctx.stroke();

      // Cozy Teal Accent Cushion
      drawPoly(
        ctx,
        [
          [10 * s, 10.8 * s],
          [14.8 * s, 12 * s],
          [12.5 * s, 15.2 * s],
          [8.2 * s, 13.5 * s],
        ],
        '#2dd4bf',
        outline,
        1 * s
      );

      // Matching Round Corduroy Ottoman Pouf in Front-Right
      const poufGrad = createRadial(
        ctx,
        21.5 * s,
        17.5 * s,
        0.5 * s,
        22.2 * s,
        18.6 * s,
        4.2 * s,
        [
          [0, '#fed7aa'],
          [0.55, '#fb923c'],
          [1, '#9a3412'],
        ],
        '#ea580c'
      );
      ctx.beginPath();
      ctx.ellipse(22.2 * s, 18.6 * s, 4.2 * s, 2.8 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = poufGrad;
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.15 * s;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(22.2 * s, 17.6 * s, 3 * s, 1.7 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#fdba74';
      ctx.fill();
      ctx.strokeStyle = '#c2410c';
      ctx.lineWidth = 0.75 * s;
      ctx.stroke();
    });
  }

  public static generateSofaT2(): HTMLCanvasElement | null {
    // 8. sofa_t2: Sofa Nỉ Đôi Hiện Đại / Fabric Loveseat (46x32 Logical -> 184x128 HD)
    // True 2.5D isometric modern mid-century 2-seater fabric loveseat with angled bolster armrests,
    // deep tufted dual seat & back cushions, two contrasting throw pillows (mustard gold & cream), tapered oak legs.
    return this.createSuperSampledCanvas(46, 32, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Occlusion Shadow
      drawPoly(
        ctx,
        [
          [15 * s, 11.5 * s],
          [44 * s, 22.8 * s],
          [28 * s, 31 * s],
          [1.5 * s, 20.2 * s],
        ],
        'rgba(15, 23, 42, 0.45)'
      );

      // Tapered Mid-Century Oak Peg Legs with Brass Tips
      drawLine(ctx, 6 * s, 19.5 * s, 4.8 * s, 23.2 * s, '#b45309', 2 * s);
      drawLine(ctx, 27.5 * s, 27.8 * s, 27.5 * s, 31 * s, '#b45309', 2 * s);
      drawLine(ctx, 40.5 * s, 21 * s, 41.8 * s, 24.8 * s, '#b45309', 2 * s);
      drawLine(ctx, 5.1 * s, 22.2 * s, 4.8 * s, 23.2 * s, '#facc15', 2 * s);
      drawLine(ctx, 27.5 * s, 29.8 * s, 27.5 * s, 31 * s, '#facc15', 2 * s);
      drawLine(ctx, 41.4 * s, 23.7 * s, 41.8 * s, 24.8 * s, '#facc15', 2 * s);

      // Left Armrest Bolster (Back-Left)
      drawPoly(
        ctx,
        [
          [3.5 * s, 11.2 * s],
          [14.5 * s, 6.4 * s],
          [19 * s, 8.2 * s],
          [19 * s, 14.8 * s],
          [8 * s, 19.5 * s],
          [3.5 * s, 17.6 * s],
        ],
        '#4338ca',
        outline,
        1.15 * s
      );

      // Tufted Dual Backrest Cushions (Isometric Extruded Back)
      const backGrad = createLinear(
        ctx,
        10 * s,
        3.5 * s,
        41 * s,
        20 * s,
        [
          [0, '#818cf8'],
          [0.55, '#4f46e5'],
          [1, '#3730a3'],
        ],
        PALETTE.midNavy
      );
      drawPoly(
        ctx,
        [
          [14 * s, 3.8 * s],
          [27.5 * s, 9.2 * s],
          [24.2 * s, 15.8 * s],
          [10.8 * s, 10.4 * s],
        ],
        backGrad,
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [27.5 * s, 9.2 * s],
          [41 * s, 14.6 * s],
          [37.8 * s, 21.2 * s],
          [24.2 * s, 15.8 * s],
        ],
        backGrad,
        outline,
        1.15 * s
      );

      // Tufted Button Dimples on Backrest
      ctx.fillStyle = outline;
      ctx.beginPath();
      ctx.arc(17.5 * s, 8.8 * s, 0.75 * s, 0, Math.PI * 2);
      ctx.arc(21.5 * s, 10.4 * s, 0.75 * s, 0, Math.PI * 2);
      ctx.arc(30.5 * s, 14 * s, 0.75 * s, 0, Math.PI * 2);
      ctx.arc(34.5 * s, 15.6 * s, 0.75 * s, 0, Math.PI * 2);
      ctx.fill();

      // Dual Plush Isometric Seat Cushions
      const seatGrad = createLinear(
        ctx,
        6 * s,
        10 * s,
        42 * s,
        26 * s,
        [
          [0, '#6366f1'],
          [0.6, '#4338ca'],
          [1, '#312e81'],
        ],
        PALETTE.midNavy
      );
      drawPoly(
        ctx,
        [
          [6 * s, 14.2 * s],
          [18 * s, 9.4 * s],
          [30.5 * s, 14.4 * s],
          [18.8 * s, 19.2 * s],
        ],
        seatGrad,
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [18.8 * s, 19.2 * s],
          [30.5 * s, 14.4 * s],
          [41.8 * s, 19 * s],
          [30 * s, 23.8 * s],
        ],
        seatGrad,
        outline,
        1.15 * s
      );

      // Front-Left & Front-Right Upholstered Base Apron + Oak Trim Rail
      drawPoly(
        ctx,
        [
          [4.2 * s, 14.8 * s],
          [27.8 * s, 24.4 * s],
          [27.8 * s, 27.8 * s],
          [4.2 * s, 18.2 * s],
        ],
        '#312e81',
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [27.8 * s, 24.4 * s],
          [41.8 * s, 18.6 * s],
          [41.8 * s, 22 * s],
          [27.8 * s, 27.8 * s],
        ],
        '#1e1b4b',
        outline,
        1.15 * s
      );
      drawLine(ctx, 4.2 * s, 18.2 * s, 27.8 * s, 27.8 * s, '#d97706', 1.4 * s);
      drawLine(ctx, 27.8 * s, 27.8 * s, 41.8 * s, 22 * s, '#b45309', 1.4 * s);

      // Two Contrasting Throw Pillows (Mustard Gold & Warm Cream)
      drawPoly(
        ctx,
        [
          [12 * s, 9.8 * s],
          [18.2 * s, 11.4 * s],
          [15.6 * s, 15.2 * s],
          [9.4 * s, 13.5 * s],
        ],
        '#facc15',
        outline,
        1.05 * s
      );
      drawPoly(
        ctx,
        [
          [31.5 * s, 15.2 * s],
          [37.8 * s, 16.8 * s],
          [35.2 * s, 20.6 * s],
          [29 * s, 18.8 * s],
        ],
        '#fef3c7',
        outline,
        1.05 * s
      );

      // Right Armrest Bolster (Front-Right) + Top Face Highlight
      drawPoly(
        ctx,
        [
          [29.2 * s, 21.5 * s],
          [40.5 * s, 16.8 * s],
          [44 * s, 18.4 * s],
          [44 * s, 23.4 * s],
          [32.6 * s, 28 * s],
          [29.2 * s, 26.4 * s],
        ],
        '#4f46e5',
        outline,
        1.15 * s
      );
      drawPoly(
        ctx,
        [
          [29.2 * s, 21.5 * s],
          [40.5 * s, 16.8 * s],
          [44 * s, 18.4 * s],
          [32.6 * s, 23 * s],
        ],
        '#818cf8',
        outline,
        0.95 * s
      );
    });
  }

  public static generateSofaT3(): HTMLCanvasElement | null {
    // Luxury Curved Velvet Sectional (56x38 Logical -> 224x152 HD)
    return this.createSuperSampledCanvas(56, 38, 4, (ctx, _w, _h, s) => {
      // Espresso Leather Backrest
      ctx.fillStyle = PALETTE.darkEspresso;
      ctx.beginPath();
      ctx.roundRect(2 * s, 4 * s, 52 * s, 14 * s, [4 * s]);
      ctx.fill();

      // Rich Mahogany Leather Seats
      ctx.fillStyle = PALETTE.richMahogany;
      ctx.beginPath();
      ctx.roundRect(4 * s, 16 * s, 48 * s, 16 * s, [3 * s]);
      ctx.fill();

      // Leather Luster Sheen
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.fillRect(8 * s, 18 * s, 18 * s, 3 * s);
      ctx.fillRect(30 * s, 18 * s, 18 * s, 3 * s);

      // Royal Plum Velvet Accent Cushions
      ctx.fillStyle = HD_PALETTE.velvetPlum;
      ctx.beginPath();
      ctx.roundRect(8 * s, 10 * s, 8 * s, 8 * s, [2 * s]);
      ctx.roundRect(40 * s, 10 * s, 8 * s, 8 * s, [2 * s]);
      ctx.fill();

      // Chrome Base Legs
      ctx.fillStyle = PALETTE.paleSilver;
      ctx.fillRect(4 * s, 34 * s, 4 * s, 3 * s);
      ctx.fillRect(48 * s, 34 * s, 4 * s, 3 * s);
    });
  }

  // =========================================================================
  // HOUSEPLANTS (Cactus, Monstera, Bonsai)
  // =========================================================================

  public static generatePlantCactus(): HTMLCanvasElement | null {
    // 9. plant_t1 / plant_cactus: Chậu Xương Rồng Sa Mạc / Potted Cactus (20x24 Logical -> 80x96 HD)
    // True 2.5D isometric sculpted terracotta pot with rim bevel and white river pebbles,
    // ribbed emerald Saguaro cactus with side arms, golden spines, and blooming desert pink flower on top.
    return this.createSuperSampledCanvas(20, 24, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Shadow & Terracotta Saucer
      ctx.beginPath();
      ctx.ellipse(10 * s, 21.2 * s, 6.2 * s, 2.2 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(15, 23, 42, 0.42)';
      ctx.fill();

      ctx.beginPath();
      ctx.ellipse(10 * s, 20.2 * s, 5 * s, 1.8 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#9a3412';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.9 * s;
      ctx.stroke();

      // Sculpted 2.5D Tapered Terracotta Pot
      const potGrad = createLinear(
        ctx,
        4 * s,
        13 * s,
        16 * s,
        21 * s,
        [
          [0, '#fb923c'],
          [0.5, '#ea580c'],
          [1, '#9a3412'],
        ],
        HD_PALETTE.terracottaBase
      );
      drawPoly(
        ctx,
        [
          [4.8 * s, 13.5 * s],
          [15.2 * s, 13.5 * s],
          [13.6 * s, 20 * s],
          [6.4 * s, 20 * s],
        ],
        potGrad,
        outline,
        1.1 * s
      );

      // 3D Pot Rim Collar Band & Top Ellipse
      drawPoly(
        ctx,
        [
          [4.2 * s, 12.6 * s],
          [15.8 * s, 12.6 * s],
          [15.4 * s, 14.4 * s],
          [4.6 * s, 14.4 * s],
        ],
        potGrad,
        outline,
        1.05 * s
      );
      ctx.beginPath();
      ctx.ellipse(10 * s, 12.6 * s, 5.8 * s, 1.9 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#fdba74';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.05 * s;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(10 * s, 12.7 * s, 4.5 * s, 1.3 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#451a03';
      ctx.fill();

      // White River Pebbles
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(7.6 * s, 12.8 * s, 0.8 * s, 0, Math.PI * 2);
      ctx.arc(12.2 * s, 12.9 * s, 0.85 * s, 0, Math.PI * 2);
      ctx.arc(10.2 * s, 13.2 * s, 0.7 * s, 0, Math.PI * 2);
      ctx.fill();

      // Left & Right Saguaro Cactus Side Arms (with Crisp #1e1b2e Outlines)
      drawLine(ctx, 7.5 * s, 9.8 * s, 4.8 * s, 9.8 * s, outline, 3.6 * s);
      drawLine(ctx, 4.8 * s, 9.8 * s, 4.8 * s, 6.8 * s, outline, 3.6 * s);
      drawLine(ctx, 7.5 * s, 9.8 * s, 4.8 * s, 9.8 * s, '#16a34a', 2 * s);
      drawLine(ctx, 4.8 * s, 9.8 * s, 4.8 * s, 6.8 * s, '#16a34a', 2 * s);

      drawLine(ctx, 12.5 * s, 8.8 * s, 15.2 * s, 8.8 * s, outline, 3.6 * s);
      drawLine(ctx, 15.2 * s, 8.8 * s, 15.2 * s, 6 * s, outline, 3.6 * s);
      drawLine(ctx, 12.5 * s, 8.8 * s, 15.2 * s, 8.8 * s, '#15803d', 2 * s);
      drawLine(ctx, 15.2 * s, 8.8 * s, 15.2 * s, 6 * s, '#15803d', 2 * s);

      // Central Plump Ribbed Saguaro Cactus Trunk
      const cactusGrad = createLinear(
        ctx,
        7 * s,
        4 * s,
        13 * s,
        13 * s,
        [
          [0, '#4ade80'],
          [0.5, '#16a34a'],
          [1, '#14532d'],
        ],
        PALETTE.oliveFern
      );
      ctx.beginPath();
      ctx.roundRect(7.4 * s, 4.2 * s, 5.2 * s, 8.8 * s, [2.6 * s]);
      ctx.fillStyle = cactusGrad;
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.15 * s;
      ctx.stroke();

      // Rib Highlight & Golden Spines
      drawLine(ctx, 9.2 * s, 5.2 * s, 9.2 * s, 12 * s, '#86efac', 0.9 * s);
      drawLine(ctx, 11 * s, 5.2 * s, 11 * s, 12 * s, '#14532d', 0.85 * s);
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.arc(8.2 * s, 7.5 * s, 0.55 * s, 0, Math.PI * 2);
      ctx.arc(11.6 * s, 9.2 * s, 0.55 * s, 0, Math.PI * 2);
      ctx.arc(4.8 * s, 7.6 * s, 0.5 * s, 0, Math.PI * 2);
      ctx.arc(15.2 * s, 6.8 * s, 0.5 * s, 0, Math.PI * 2);
      ctx.fill();

      // Blooming Desert Pink Flower on Top
      ctx.fillStyle = '#f43f5e';
      ctx.strokeStyle = outline;
      ctx.lineWidth = 0.75 * s;
      for (const [fx, fy, fr] of [
        [9, 3.6, 1.3],
        [11, 3.6, 1.3],
        [10, 2.8, 1.4],
      ]) {
        ctx.beginPath();
        ctx.arc(fx * s, fy * s, fr * s, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.arc(10 * s, 3.6 * s, 0.85 * s, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  public static generatePlantMonstera(): HTMLCanvasElement | null {
    // 10. plant_t2 / plant_monstera: Chậu Trầu Bà Lá Xẻ / Monstera Deliciosa (24x32 Logical -> 96x128 HD)
    // True 2.5D isometric ceramic cream planter resting on a mid-century wooden tripod stand,
    // rich potting soil, and multi-layered fenestrated Monstera leaves with deep emerald-to-lime gradients, veins, highlights.
    return this.createSuperSampledCanvas(24, 32, 4, (ctx, _w, _h, s) => {
      const outline = '#1e1b2e';

      // Ground Ambient Shadow
      ctx.beginPath();
      ctx.ellipse(12 * s, 29.5 * s, 6.5 * s, 2.2 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(15, 23, 42, 0.42)';
      ctx.fill();

      // Mid-Century Wooden Tripod Stand
      drawLine(ctx, 8 * s, 24.5 * s, 6.5 * s, 29.5 * s, '#b45309', 1.8 * s);
      drawLine(ctx, 16 * s, 24.5 * s, 17.5 * s, 29.5 * s, '#b45309', 1.8 * s);
      drawLine(ctx, 12 * s, 25 * s, 12 * s, 30 * s, '#78350f', 1.8 * s);
      drawLine(ctx, 7.2 * s, 26.8 * s, 16.8 * s, 26.8 * s, '#78350f', 1.4 * s);

      // 2.5D Glazed Cream Ceramic Planter Cylinder
      const potGrad = createLinear(
        ctx,
        7 * s,
        17.5 * s,
        17 * s,
        26 * s,
        [
          [0, '#ffffff'],
          [0.65, '#e2e8f0'],
          [1, '#94a3b8'],
        ],
        PALETTE.frostWhite
      );
      drawPoly(
        ctx,
        [
          [7 * s, 17.8 * s],
          [17 * s, 17.8 * s],
          [16.2 * s, 25.5 * s],
          [7.8 * s, 25.5 * s],
        ],
        potGrad,
        outline,
        1.15 * s
      );
      // Fluted Ceramic Vertical Ribs
      drawLine(ctx, 9.6 * s, 19 * s, 9.8 * s, 25 * s, '#cbd5e1', 0.75 * s);
      drawLine(ctx, 12 * s, 19.2 * s, 12 * s, 25.2 * s, '#cbd5e1', 0.75 * s);
      drawLine(ctx, 14.4 * s, 19 * s, 14.2 * s, 25 * s, '#94a3b8', 0.75 * s);

      ctx.beginPath();
      ctx.ellipse(12 * s, 17.8 * s, 5 * s, 1.6 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#f8fafc';
      ctx.fill();
      ctx.strokeStyle = outline;
      ctx.lineWidth = 1.05 * s;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(12 * s, 17.9 * s, 3.9 * s, 1.1 * s, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#3f2314';
      ctx.fill();

      // Back Dark-Emerald Foliage Layer
      drawPoly(
        ctx,
        [
          [12 * s, 17.5 * s],
          [6.5 * s, 13.5 * s],
          [4.5 * s, 7.5 * s],
          [8.8 * s, 5.5 * s],
          [11.8 * s, 10.5 * s],
        ],
        '#064e3b',
        outline,
        1 * s
      );
      drawPoly(
        ctx,
        [
          [12 * s, 17.5 * s],
          [17.5 * s, 13.5 * s],
          [19.5 * s, 7.5 * s],
          [15.2 * s, 5.5 * s],
          [12.2 * s, 10.5 * s],
        ],
        '#065f46',
        outline,
        1 * s
      );

      // Multi-Layered Fenestrated Monstera Leaves
      // Left Emerald Split Leaf
      const leaf1Grad = createLinear(
        ctx,
        2 * s,
        6 * s,
        12 * s,
        18 * s,
        [
          [0, '#4ade80'],
          [1, '#15803d'],
        ],
        HD_PALETTE.monsteraMid
      );
      drawPoly(
        ctx,
        [
          [11.5 * s, 17.5 * s],
          [5 * s, 14.5 * s],
          [2.8 * s, 9.5 * s],
          [6.5 * s, 6.5 * s],
          [10.5 * s, 11 * s],
        ],
        leaf1Grad,
        outline,
        1.1 * s
      );
      drawLine(ctx, 11 * s, 16.5 * s, 5.2 * s, 8.5 * s, '#bbf7d0', 0.9 * s);
      // Fenestration splits on left leaf
      drawLine(ctx, 4.2 * s, 11.2 * s, 6.4 * s, 10.8 * s, outline, 0.85 * s);
      drawLine(ctx, 5.4 * s, 9.2 * s, 7.4 * s, 9 * s, outline, 0.85 * s);

      // Right Emerald Split Leaf
      const leaf2Grad = createLinear(
        ctx,
        12 * s,
        6 * s,
        22 * s,
        18 * s,
        [
          [0, '#22c55e'],
          [1, '#14532d'],
        ],
        HD_PALETTE.monsteraDark
      );
      drawPoly(
        ctx,
        [
          [12.5 * s, 17.5 * s],
          [19 * s, 14.5 * s],
          [21.2 * s, 9.5 * s],
          [17.5 * s, 6.5 * s],
          [13.5 * s, 11 * s],
        ],
        leaf2Grad,
        outline,
        1.1 * s
      );
      drawLine(ctx, 13 * s, 16.5 * s, 18.8 * s, 8.5 * s, '#86efac', 0.9 * s);
      // Fenestration splits on right leaf
      drawLine(ctx, 19.8 * s, 11.2 * s, 17.6 * s, 10.8 * s, outline, 0.85 * s);
      drawLine(ctx, 18.6 * s, 9.2 * s, 16.6 * s, 9 * s, outline, 0.85 * s);

      // Center Hero Glossy Monstera Leaf
      const leaf3Grad = createLinear(
        ctx,
        9 * s,
        2 * s,
        15 * s,
        17 * s,
        [
          [0, '#86efac'],
          [0.5, '#22c55e'],
          [1, '#15803d'],
        ],
        HD_PALETTE.monsteraBright
      );
      drawPoly(
        ctx,
        [
          [12 * s, 17.5 * s],
          [8.5 * s, 10.5 * s],
          [9.8 * s, 4 * s],
          [12 * s, 2.2 * s],
          [14.2 * s, 4 * s],
          [15.5 * s, 10.5 * s],
        ],
        leaf3Grad,
        outline,
        1.15 * s
      );
      drawLine(ctx, 12 * s, 16.5 * s, 12 * s, 3.8 * s, '#dcfce7', 1 * s);
      // Fenestration splits on center leaf
      drawLine(ctx, 9.2 * s, 7.6 * s, 11 * s, 8.2 * s, outline, 0.85 * s);
      drawLine(ctx, 14.8 * s, 7.6 * s, 13 * s, 8.2 * s, outline, 0.85 * s);
    });
  }

  public static generatePlantBonsai(): HTMLCanvasElement | null {
    // Bonsai in Ceramic Tray (26x28 Logical -> 104x112 HD)
    return this.createSuperSampledCanvas(26, 28, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      drawEllipse(ctx, 13 * s, 24.5 * s, 9.5 * s, 3 * s, 'rgba(61, 37, 30, 0.22)');

      // Hand-drawn Isometric Ceramic Bonsai Tray
      drawIsoPrism(
        ctx,
        13 * s,
        21.5 * s,
        8.5 * s,
        4 * s,
        3.5 * s,
        '#7c5846',
        '#9c725b',
        '#5c3e30',
        outline,
        1.1 * s
      );
      drawEllipse(ctx, 13 * s, 21.5 * s, 7 * s, 2.8 * s, '#65a30d', outline, 0.9 * s);

      // Gnarled Driftwood Trunk with #3D251E outline
      drawPoly(
        ctx,
        [
          [11 * s, 21.5 * s],
          [10 * s, 15 * s],
          [7.5 * s, 11 * s],
          [9.5 * s, 9.5 * s],
          [13.5 * s, 12.5 * s],
          [16.5 * s, 11.5 * s],
          [15 * s, 15.5 * s],
          [14.5 * s, 21.5 * s],
        ],
        '#8c583a',
        outline,
        1.1 * s
      );

      // Cozy Gouache Foliage Clouds with #3D251E ink outlines
      drawEllipse(ctx, 8.5 * s, 8.5 * s, 5.2 * s, 3.6 * s, '#4d7c0f', outline, 1.15 * s);
      drawEllipse(ctx, 16.8 * s, 10.8 * s, 4.5 * s, 3.1 * s, '#3f6212', outline, 1.15 * s);
      drawEllipse(ctx, 12.5 * s, 6.2 * s, 5.6 * s, 3.8 * s, '#65a30d', outline, 1.15 * s);
      drawEllipse(ctx, 11.5 * s, 5.2 * s, 3.2 * s, 1.6 * s, '#a3e635');
    });
  }

  // =========================================================================
  // 5 FLAGSHIP STATE SOUVENIRS (Cozy Hand-Drawn #3D251E Ink & Gouache Style)
  // =========================================================================

  public static generateSouvenirNY(): HTMLCanvasElement | null {
    // Statue of Liberty Miniature (22x34 Logical -> 88x136 HD)
    return this.createSuperSampledCanvas(22, 34, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      drawEllipse(ctx, 11 * s, 31 * s, 8 * s, 2.6 * s, 'rgba(61, 37, 30, 0.22)');

      // Isometric Wooden & Sandstone Pedestal Base
      drawIsoPrism(
        ctx,
        11 * s,
        27 * s,
        6.8 * s,
        3.4 * s,
        4.2 * s,
        '#d6c7b2',
        '#e7dac7',
        '#b8a58e',
        outline,
        1.1 * s
      );
      drawIsoPrism(
        ctx,
        11 * s,
        22.5 * s,
        5 * s,
        2.5 * s,
        4.8 * s,
        '#c7b69e',
        '#dfd1bc',
        '#a39179',
        outline,
        1.05 * s
      );

      // Verdigris Mint Gouache Robe & Figure
      drawPoly(
        ctx,
        [
          [7.8 * s, 22.5 * s],
          [8.6 * s, 10.5 * s],
          [13.4 * s, 10.5 * s],
          [14.2 * s, 22.5 * s],
        ],
        '#6ee7b7',
        outline,
        1.1 * s
      );
      // Raised Torch Arm
      drawPoly(
        ctx,
        [
          [12.8 * s, 12.5 * s],
          [14.8 * s, 6.2 * s],
          [16.2 * s, 6.8 * s],
          [14 * s, 13.5 * s],
        ],
        '#6ee7b7',
        outline,
        1.0 * s
      );
      // Head & Crown
      drawEllipse(ctx, 11 * s, 8.2 * s, 2.4 * s, 2.4 * s, '#a7f3d0', outline, 1.05 * s);
      // Warm Golden Torch Flame
      drawEllipse(ctx, 15.5 * s, 4.4 * s, 1.9 * s, 2.4 * s, '#fbbf24', outline, 1.0 * s);
    });
  }

  public static generateSouvenirCA(): HTMLCanvasElement | null {
    // Golden Gate Bridge Model (30x24 Logical -> 120x96 HD)
    return this.createSuperSampledCanvas(30, 24, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      drawEllipse(ctx, 15 * s, 21 * s, 12 * s, 2.6 * s, 'rgba(61, 37, 30, 0.22)');

      // Warm Wooden Display Base
      drawIsoPrism(
        ctx,
        15 * s,
        17.5 * s,
        12 * s,
        3.5 * s,
        3.5 * s,
        '#c88a58',
        '#df9f6c',
        '#9c6339',
        outline,
        1.1 * s
      );

      // Coral-Red Bridge Towers with #3D251E outlines
      drawPoly(
        ctx,
        [
          [6.5 * s, 17.5 * s],
          [7 * s, 3.5 * s],
          [10 * s, 3.5 * s],
          [10.5 * s, 17.5 * s],
        ],
        '#ea580c',
        outline,
        1.1 * s
      );
      drawPoly(
        ctx,
        [
          [19.5 * s, 17.5 * s],
          [20 * s, 3.5 * s],
          [23 * s, 3.5 * s],
          [23.5 * s, 17.5 * s],
        ],
        '#ea580c',
        outline,
        1.1 * s
      );

      // Suspension Cables & Deck
      drawLine(ctx, 3 * s, 13 * s, 27 * s, 13 * s, outline, 1.8 * s);
      drawLine(ctx, 3 * s, 12.6 * s, 27 * s, 12.6 * s, '#fb923c', 1.1 * s);
      drawLine(ctx, 8.5 * s, 4.5 * s, 15 * s, 11.5 * s, outline, 1.05 * s);
      drawLine(ctx, 21.5 * s, 4.5 * s, 15 * s, 11.5 * s, outline, 1.05 * s);
    });
  }

  public static generateSouvenirTX(): HTMLCanvasElement | null {
    // Longhorn Trophy Plaque (28x22 Logical -> 112x88 HD)
    return this.createSuperSampledCanvas(28, 22, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      drawEllipse(ctx, 14 * s, 19 * s, 10 * s, 2.5 * s, 'rgba(61, 37, 30, 0.22)');

      // Warm Walnut Plaque Stand
      drawIsoPrism(
        ctx,
        14 * s,
        15.5 * s,
        8.5 * s,
        3.2 * s,
        3.6 * s,
        '#9c5d38',
        '#b87349',
        '#784222',
        outline,
        1.1 * s
      );

      // Sweeping Cream Horns with #3D251E outline
      drawPoly(
        ctx,
        [
          [2.5 * s, 5.5 * s],
          [10.5 * s, 9.5 * s],
          [17.5 * s, 9.5 * s],
          [25.5 * s, 5.5 * s],
          [24 * s, 11 * s],
          [17.5 * s, 12.5 * s],
          [10.5 * s, 12.5 * s],
          [4 * s, 11 * s],
        ],
        '#fef3c7',
        outline,
        1.1 * s
      );

      // Center Steer Medallion & Gold Lone Star
      drawEllipse(ctx, 14 * s, 11.5 * s, 3.8 * s, 4.2 * s, '#fffbeb', outline, 1.1 * s);
      drawEllipse(ctx, 14 * s, 11.2 * s, 1.6 * s, 1.6 * s, '#fbbf24', outline, 0.85 * s);
    });
  }

  public static generateSouvenirFL(): HTMLCanvasElement | null {
    // NASA Rocket & Flamingo (22x36 Logical -> 88x144 HD)
    return this.createSuperSampledCanvas(22, 36, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      drawEllipse(ctx, 11 * s, 33 * s, 8 * s, 2.5 * s, 'rgba(61, 37, 30, 0.22)');

      // Isometric Wooden Launchpad Base
      drawIsoPrism(
        ctx,
        11 * s,
        29.5 * s,
        7.2 * s,
        3.2 * s,
        3.5 * s,
        '#c88a58',
        '#df9f6c',
        '#9c6339',
        outline,
        1.1 * s
      );

      // Saturn V Rocket Body with #3D251E ink outlines
      drawPoly(
        ctx,
        [
          [7.5 * s, 29.5 * s],
          [8 * s, 7.5 * s],
          [11 * s, 2.5 * s],
          [14 * s, 7.5 * s],
          [14.5 * s, 29.5 * s],
        ],
        '#fffbeb',
        outline,
        1.15 * s
      );
      // Red Nose Cone & Coral Fin
      drawPoly(
        ctx,
        [
          [8.3 * s, 7.5 * s],
          [11 * s, 2.5 * s],
          [13.7 * s, 7.5 * s],
        ],
        '#ef4444',
        outline,
        1.05 * s
      );
      // Pink Flamingo Companion
      drawEllipse(ctx, 16.8 * s, 25 * s, 2.4 * s, 1.8 * s, '#f472b6', outline, 1.0 * s);
    });
  }

  public static generateSouvenirWA(): HTMLCanvasElement | null {
    // Space Needle Miniature (20x38 Logical -> 80x152 HD)
    return this.createSuperSampledCanvas(20, 38, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      drawEllipse(ctx, 10 * s, 35 * s, 7.5 * s, 2.4 * s, 'rgba(61, 37, 30, 0.22)');

      // Warm Wooden Pedestal Base
      drawIsoPrism(
        ctx,
        10 * s,
        31.5 * s,
        6.5 * s,
        3 * s,
        3.5 * s,
        '#c88a58',
        '#df9f6c',
        '#9c6339',
        outline,
        1.1 * s
      );

      // Tapered Space Needle Tower Legs
      drawPoly(
        ctx,
        [
          [6.8 * s, 31.5 * s],
          [8.8 * s, 12.5 * s],
          [11.2 * s, 12.5 * s],
          [13.2 * s, 31.5 * s],
        ],
        '#fffbeb',
        outline,
        1.1 * s
      );

      // Flying Saucer Observation Deck
      drawEllipse(ctx, 10 * s, 11 * s, 7.2 * s, 2.8 * s, '#38bdf8', outline, 1.15 * s);
      drawEllipse(ctx, 10 * s, 9.6 * s, 4.5 * s, 1.6 * s, '#fef08a', outline, 0.95 * s);

      // Spire Needle
      drawLine(ctx, 10 * s, 8 * s, 10 * s, 2.2 * s, outline, 1.2 * s);
    });
  }

  // =========================================================================
  // UTILITY / CURSOR ASSETS
  // =========================================================================

  public static generateTileCursor(): HTMLCanvasElement | null {
    // Holographic Cyan Isometric Tile Cursor (40x20 Logical -> 160x80 HD)
    return this.createSuperSampledCanvas(40, 20, 4, (ctx, _w, _h, s) => {
      ctx.beginPath();
      ctx.moveTo(20 * s, 0);
      ctx.lineTo(39 * s, 9.5 * s);
      ctx.lineTo(20 * s, 19 * s);
      ctx.lineTo(0.5 * s, 9.5 * s);
      ctx.closePath();

      ctx.fillStyle = 'rgba(69, 195, 184, 0.25)';
      ctx.fill();

      ctx.strokeStyle = PALETTE.neonCyan;
      ctx.lineWidth = 1.5 * s;
      ctx.stroke();
    });
  }

  public static generateShadowOval(): HTMLCanvasElement | null {
    // Ambient Occlusion Shadow Oval (18x10 Logical -> 72x40 HD)
    return this.createSuperSampledCanvas(18, 10, 4, (ctx, _w, _h, s) => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.beginPath();
      ctx.ellipse(9 * s, 5 * s, 8 * s, 4 * s, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  public static generateCozyRoomShell(): HTMLCanvasElement | null {
    // Cohesive Hand-Drawn Isometric Room Diorama Shell (256x256 Logical -> 1024x1024 HD)
    // Hydrated with ./assets/furniture/cozy_room_shell.png at 1024x1024 full frame.
    return this.createSuperSampledCanvas(256, 256, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      // Isometric floor diamond fallback matching exact normalized coords (top apex at 128, 123.25)
      drawPoly(
        ctx,
        [
          [128 * s, 123.25 * s],
          [227.25 * s, 174.75 * s],
          [128 * s, 226.25 * s],
          [28.75 * s, 174.75 * s],
        ],
        '#D99B66',
        outline,
        1.5 * s
      );
      // Left & right walls fallback
      drawPoly(
        ctx,
        [
          [28.75 * s, 64 * s],
          [128 * s, 14 * s],
          [128 * s, 123.25 * s],
          [28.75 * s, 174.75 * s],
        ],
        '#FFF5E4',
        outline,
        1.5 * s
      );
      drawPoly(
        ctx,
        [
          [128 * s, 14 * s],
          [227.25 * s, 64 * s],
          [227.25 * s, 174.75 * s],
          [128 * s, 123.25 * s],
        ],
        '#F7E4C8',
        outline,
        1.5 * s
      );
    });
  }

  public static generateDeskChairCozy(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Wooden Desk Chair (28x32 Logical -> 112x128 HD)
    // Hydrated with ./assets/furniture/desk_chair_cozy.png
    return this.createSuperSampledCanvas(28, 32, 4, (ctx, _w, _h, s) => {
      const outline = '#3D251E';
      // Wooden legs & crossbars
      drawLine(ctx, 7 * s, 18 * s, 6 * s, 28 * s, '#C87D4B', 2.0 * s);
      drawLine(ctx, 14 * s, 21 * s, 14 * s, 30 * s, '#B36A3C', 2.2 * s);
      drawLine(ctx, 21 * s, 18 * s, 22 * s, 27 * s, '#C87D4B', 2.0 * s);
      // Cream-peach seat cushion
      drawPoly(
        ctx,
        [
          [7 * s, 16 * s],
          [15 * s, 12 * s],
          [22 * s, 16 * s],
          [14 * s, 21 * s],
        ],
        '#FFF5E4',
        outline,
        1.1 * s
      );
      // Curved honey-oak backrest in foreground (bottom-left facing top-right)
      drawPoly(
        ctx,
        [
          [5 * s, 6 * s],
          [15 * s, 11 * s],
          [15 * s, 16 * s],
          [5 * s, 11 * s],
        ],
        '#D99B66',
        outline,
        1.1 * s
      );
    });
  }

  public static generatePlayerCozy(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Cozy Chibi Character (32x48 Logical -> 128x192 HD)
    // Hydrated with ./assets/furniture/player_cozy.png
    return this.createSuperSampledCanvas(32, 48, 4, (ctx, _w, _h, s) => {
      this.drawChibiVectorFrame(ctx, 0, 0, 0, 0, -1, s * 2);
    });
  }

  public static generatePlayerCozyWalk1(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Cozy Chibi Character Front Walk Stride 1 (32x48 Logical -> 128x192 HD)
    // Hydrated with ./assets/furniture/player_cozy_walk1.png
    return this.createSuperSampledCanvas(32, 48, 4, (ctx, _w, _h, s) => {
      this.drawChibiVectorFrame(ctx, 0, 0, 0, 1, 1, s * 2);
    });
  }

  public static generatePlayerCozyWalk2(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Cozy Chibi Character Front Walk Stride 2 (32x48 Logical -> 128x192 HD)
    // Hydrated with ./assets/furniture/player_cozy_walk2.png
    return this.createSuperSampledCanvas(32, 48, 4, (ctx, _w, _h, s) => {
      this.drawChibiVectorFrame(ctx, 0, 0, 0, 1, -1, s * 2);
    });
  }

  public static generatePlayerCozyBack(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Cozy Chibi Character Back View (32x48 Logical -> 128x192 HD)
    // Hydrated with ./assets/furniture/player_cozy_back.png
    return this.createSuperSampledCanvas(32, 48, 4, (ctx, _w, _h, s) => {
      this.drawChibiVectorFrame(ctx, 0, 0, 2, 0, -1, s * 2);
    });
  }

  public static generatePlayerCozyBackWalk1(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Cozy Chibi Character Back Walk Stride 1 (32x48 Logical -> 128x192 HD)
    // Hydrated with ./assets/furniture/player_cozy_back_walk1.png
    return this.createSuperSampledCanvas(32, 48, 4, (ctx, _w, _h, s) => {
      this.drawChibiVectorFrame(ctx, 0, 0, 2, 1, 1, s * 2);
    });
  }

  public static generatePlayerCozyBackWalk2(): HTMLCanvasElement | null {
    // Hand-Drawn Isometric Cozy Chibi Character Back Walk Stride 2 (32x48 Logical -> 128x192 HD)
    // Hydrated with ./assets/furniture/player_cozy_back_walk2.png
    return this.createSuperSampledCanvas(32, 48, 4, (ctx, _w, _h, s) => {
      this.drawChibiVectorFrame(ctx, 0, 0, 2, 1, -1, s * 2);
    });
  }

  // =========================================================================
  // MASTER REGISTRATION INTO PHASER TEXTURE MANAGER
  // =========================================================================

  public static readonly PNG_FURNITURE_SPRITE_URLS: Record<string, string> = {
    cozy_bed: './assets/furniture/cozy_bed.png',
    wooden_cabinet: './assets/furniture/wooden_cabinet.png',
    bed_t1: './assets/furniture/bed_t1.png',
    bed_t2: './assets/furniture/cozy_bed.png',
    bed_t3: './assets/furniture/bed_t3.png',
    fridge_t1: './assets/furniture/fridge_t1.png',
    fridge_t2: './assets/furniture/fridge_t2.png',
    fridge_t3: './assets/furniture/fridge_t3.png',
    desk_t1: './assets/furniture/desk_t1.png',
    desk_t2: './assets/furniture/desk_t2.png',
    desk_t3: './assets/furniture/desk_t3.png',
    sofa_t1: './assets/furniture/sofa_t1.png',
    sofa_t2: './assets/furniture/sofa_t2.png',
    sofa_t3: './assets/furniture/sofa_t3.png',
    plant_cactus: './assets/furniture/plant_t1.png',
    plant_succulent: './assets/furniture/plant_t1.png',
    plant_monstera: './assets/furniture/plant_t2.png',
    plant_ficus: './assets/furniture/plant_t2.png',
    plant_bonsai: './assets/furniture/plant_bonsai.png',
    plant_t1: './assets/furniture/plant_t1.png',
    plant_t2: './assets/furniture/plant_t2.png',
    plant_t3: './assets/furniture/plant_t3.png',
    souvenir_ny_statue_liberty: './assets/souvenirs/souvenir_ny_statue_liberty.png',
    souvenir_ny_statue: './assets/souvenirs/souvenir_ny_statue_liberty.png',
    souvenir_ca_golden_gate: './assets/souvenirs/souvenir_ca_golden_gate.png',
    souvenir_ca_bridge: './assets/souvenirs/souvenir_ca_golden_gate.png',
    souvenir_tx_longhorn: './assets/souvenirs/souvenir_tx_longhorn.png',
    souvenir_tx_hat: './assets/souvenirs/souvenir_tx_longhorn.png',
    souvenir_fl_rocket_flamingo: './assets/souvenirs/souvenir_fl_rocket_flamingo.png',
    souvenir_fl_rocket: './assets/souvenirs/souvenir_fl_rocket_flamingo.png',
    souvenir_wa_space_needle: './assets/souvenirs/souvenir_wa_space_needle.png',
    souvenir_wa_needle: './assets/souvenirs/souvenir_wa_space_needle.png',
    souvenir_az_grand_canyon: './assets/souvenirs/souvenir_az_grand_canyon.png',
    souvenir_or_crater_lake: './assets/souvenirs/souvenir_or_crater_lake.png',
    souvenir_id_star_garnet: './assets/souvenirs/souvenir_id_star_garnet.png',
    souvenir_nv_hoover_dam: './assets/souvenirs/souvenir_nv_hoover_dam.png',
    souvenir_co_rocky_peaks: './assets/souvenirs/souvenir_co_rocky_peaks.png',
    souvenir_mo_gateway_arch: './assets/souvenirs/souvenir_mo_gateway_arch.png',
    souvenir_hi_aloha_volcano: './assets/souvenirs/souvenir_hi_aloha_volcano.png',
    souvenir_ak_aurora_totem: './assets/souvenirs/souvenir_ak_aurora_totem.png',
    souvenir_dc_capitol_dome: './assets/souvenirs/souvenir_dc_capitol_dome.png',
    souvenir_forest_smoky_mill: './assets/souvenirs/souvenir_forest_smoky_mill.png',
    window_day: './assets/environment/window_day.png',
    window_sunset: './assets/environment/window_sunset.png',
    window_night: './assets/environment/window_night.png',
    icon_atlas_compass: './assets/ui_icons/icon_atlas_compass.png',
    icon_career_briefcase: './assets/ui_icons/icon_career_briefcase.png',
    icon_pantry_platter: './assets/ui_icons/icon_pantry_platter.png',
    icon_decor_palette: './assets/ui_icons/icon_decor_palette.png',
    cozy_room_shell: './assets/furniture/cozy_room_shell.png',
    desk_chair_cozy: './assets/furniture/desk_chair_cozy.png',
    player_cozy: './assets/furniture/player_cozy.png',
    player_cozy_walk1: './assets/furniture/player_cozy_walk1.png',
    player_cozy_walk2: './assets/furniture/player_cozy_walk2.png',
    player_cozy_back: './assets/furniture/player_cozy_back.png',
    player_cozy_back_walk1: './assets/furniture/player_cozy_back_walk1.png',
    player_cozy_back_walk2: './assets/furniture/player_cozy_back_walk2.png',
  };

  /**
   * Tight non-transparent content bounding boxes [sx, sy, sw, sh] inside each PNG
   * so non-square furniture canvases fit the illustration at 1:1 aspect ratio without squashing/stretching.
   */
  public static readonly PNG_FURNITURE_CONTENT_BBOX: Record<string, [number, number, number, number]> = {
    cozy_bed: [56, 83, 399, 346],
    wooden_cabinet: [71, 35, 370, 442],
    bed_t1: [35, 121, 442, 270],
    bed_t2: [56, 83, 399, 346],
    bed_t3: [35, 47, 442, 418],
    fridge_t1: [131, 35, 250, 442],
    fridge_t2: [151, 35, 210, 442],
    fridge_t3: [131, 35, 249, 442],
    desk_t1: [36, 35, 439, 442],
    desk_t2: [35, 38, 442, 436],
    desk_t3: [35, 38, 442, 436],
    sofa_t1: [35, 56, 442, 399],
    sofa_t2: [52, 69, 407, 374],
    sofa_t3: [35, 70, 442, 371],
    plant_t1: [89, 35, 333, 442],
    plant_t2: [83, 35, 345, 442],
    plant_t3: [71, 35, 370, 442],
    plant_cactus: [89, 35, 333, 442],
    plant_succulent: [89, 35, 333, 442],
    plant_monstera: [83, 35, 345, 442],
    plant_ficus: [83, 35, 345, 442],
    plant_bonsai: [71, 35, 370, 442],
    souvenir_ny_statue_liberty: [154, 35, 204, 442],
    souvenir_ny_statue: [154, 35, 204, 442],
    souvenir_ca_golden_gate: [44, 35, 423, 442],
    souvenir_ca_bridge: [44, 35, 423, 442],
    souvenir_tx_longhorn: [48, 35, 415, 442],
    souvenir_tx_hat: [48, 35, 415, 442],
    souvenir_fl_rocket_flamingo: [73, 35, 366, 442],
    souvenir_fl_rocket: [73, 35, 366, 442],
    souvenir_wa_space_needle: [88, 35, 336, 442],
    souvenir_wa_needle: [88, 35, 336, 442],
    souvenir_az_grand_canyon: [44, 35, 425, 443],
    souvenir_or_crater_lake: [35, 46, 443, 421],
    souvenir_id_star_garnet: [39, 35, 435, 443],
    souvenir_nv_hoover_dam: [43, 35, 427, 443],
    souvenir_co_rocky_peaks: [43, 35, 427, 443],
    souvenir_mo_gateway_arch: [44, 35, 425, 443],
    souvenir_hi_aloha_volcano: [35, 50, 443, 413],
    souvenir_ak_aurora_totem: [44, 35, 425, 443],
    souvenir_dc_capitol_dome: [43, 35, 427, 443],
    souvenir_forest_smoky_mill: [41, 35, 431, 443],
    window_day: [90, 35, 332, 442],
    window_sunset: [89, 35, 333, 442],
    window_night: [88, 35, 335, 442],
    icon_atlas_compass: [35, 54, 442, 404],
    icon_career_briefcase: [35, 49, 442, 414],
    icon_pantry_platter: [35, 75, 442, 361],
    icon_decor_palette: [35, 60, 442, 391],
    cozy_room_shell: [0, 0, 1024, 1024],
    desk_chair_cozy: [80, 36, 354, 441],
    player_cozy: [136, 42, 240, 435],
    player_cozy_walk1: [136, 42, 240, 435],
    player_cozy_walk2: [136, 42, 240, 435],
    player_cozy_back: [136, 42, 240, 435],
    player_cozy_back_walk1: [136, 42, 240, 435],
    player_cozy_back_walk2: [136, 42, 240, 435],
  };

  /**
   * Draws a 512x512 furniture PNG into a target canvas context preserving 1:1 aspect ratio
   * (object-fit: contain, bottom-center aligned) using its tight non-transparent bounding box.
   */
  public static drawPngFittedToContext(
    ctx: CanvasRenderingContext2D,
    img: CanvasImageSource,
    targetW: number,
    targetH: number,
    key: string,
    sourceWidth: number = 512,
    sourceHeight: number = 512
  ): void {
    const bbox = this.PNG_FURNITURE_CONTENT_BBOX[key];
    const sx = bbox ? bbox[0] : 0;
    const sy = bbox ? bbox[1] : 0;
    const sw = bbox ? bbox[2] : sourceWidth;
    const sh = bbox ? bbox[3] : sourceHeight;

    const scale = Math.min(targetW / sw, targetH / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    const dx = (targetW - dw) / 2;
    const dy = targetH - dh;

    ctx.clearRect(0, 0, targetW, targetH);
    ctx.imageSmoothingEnabled = true;
    if ('imageSmoothingQuality' in ctx) {
      ctx.imageSmoothingQuality = 'high';
    }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  /**
   * Asynchronously hydrates a synthesized dual-canvas with the hand-drawn .png illustration
   * in real browser environments while keeping synchronous dimensions intact for unit tests.
   */
  public static hydrateCanvasWithPng(
    canvas: HTMLCanvasElement | null,
    key: string,
    onHydrated?: () => void
  ): void {
    if (!canvas) return;
    const pngUrl = this.PNG_FURNITURE_SPRITE_URLS[key];
    if (!pngUrl) return;
    if (typeof window === 'undefined' || typeof Image === 'undefined') return;
    if ((window as unknown as { happyDOM?: unknown }).happyDOM) return;

    try {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        const natW = img.naturalWidth || 512;
        const natH = img.naturalHeight || 512;
        const hdCanvas = (canvas as unknown as { __hdCanvas?: HTMLCanvasElement }).__hdCanvas;
        if (hdCanvas) {
          const hdCtx = hdCanvas.getContext('2d');
          if (hdCtx) {
            this.drawPngFittedToContext(hdCtx, img, hdCanvas.width, hdCanvas.height, key, natW, natH);
          }
        }
        const logicalCtx = canvas.getContext('2d');
        if (logicalCtx) {
          this.drawPngFittedToContext(logicalCtx, img, canvas.width, canvas.height, key, natW, natH);
        }
        if (onHydrated) {
          onHydrated();
        }
      };
      img.src = pngUrl;
    } catch {
      // Ignore image loading errors in non-browser test runners
    }
  }

  /**
   * Hydrates all 24 frames (6 cols x 4 rows) of the player_chibi spritesheet with the hand-drawn
   * player_cozy.png illustration so directional walk/idle frames use the cohesive #3D251E character.
   */
  public static hydratePlayerSpritesheetWithPng(
    canvas: HTMLCanvasElement | null,
    onHydrated?: () => void
  ): void {
    if (!canvas) return;
    if (typeof window === 'undefined' || typeof Image === 'undefined') return;
    if ((window as unknown as { happyDOM?: unknown }).happyDOM) return;

    try {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        const bbox = this.PNG_FURNITURE_CONTENT_BBOX.player_cozy || [159, 42, 193, 435];
        const [sx, sy, sw, sh] = bbox;

        const stampFrames = (targetCanvas: HTMLCanvasElement, scale: number) => {
          const ctx = targetCanvas.getContext('2d');
          if (!ctx) return;
          const cellW = 16 * scale;
          const cellH = 24 * scale;
          ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
          ctx.imageSmoothingEnabled = true;
          if ('imageSmoothingQuality' in ctx) {
            ctx.imageSmoothingQuality = 'high';
          }

          for (let row = 0; row < 4; row++) {
            const isFacingRight = row === 0 || row === 2;
            for (let col = 0; col < 6; col++) {
              const ox = col * cellW;
              const oy = row * cellH;
              const isBob = col === 1 || col === 3 || col === 5;
              const bobPx = isBob ? 0.65 * scale : 0;

              const fitScale = Math.min((cellW * 0.92) / sw, ((cellH - 1.2 * scale) * 0.96) / sh);
              const dw = sw * fitScale;
              const dh = sh * fitScale;

              ctx.save();
              ctx.beginPath();
              ctx.rect(ox, oy, cellW, cellH);
              ctx.clip();

              const cx = ox + cellW / 2;
              const cy = oy + cellH - dh / 2 + bobPx * 0.5;
              ctx.translate(cx, cy);
              if (!isFacingRight) {
                ctx.scale(-1, 1);
              }
              ctx.drawImage(img, sx, sy, sw, sh, -dw / 2, -dh / 2, dw, dh);
              ctx.restore();
            }
          }
        };

        const hdCanvas = (canvas as unknown as { __hdCanvas?: HTMLCanvasElement }).__hdCanvas;
        const s = (canvas as unknown as { __scale?: number }).__scale || 4;
        if (hdCanvas) {
          stampFrames(hdCanvas, s);
        }
        stampFrames(canvas, 1);

        if (onHydrated) {
          onHydrated();
        }
      };
      img.src = this.PNG_FURNITURE_SPRITE_URLS.player_cozy;
    } catch {
      // Ignore image loading errors in non-browser test runners
    }
  }

  /**
   * Builds all canvases and registers them into Phaser's Texture Manager.
   * Ensures Zero Missing Assets across the entire application.
   */
  public static generateAll(scene: Phaser.Scene): void {
    if (!scene || !scene.textures) return;

    const textures: Array<{ key: string; canvas: HTMLCanvasElement | null; aliases?: string[] }> = [
      // Floors
      { key: 'floor_wood', canvas: this.generateWoodFloor(), aliases: ['tile_wood'] },
      { key: 'floor_carpet', canvas: this.generateCarpetFloor(), aliases: ['tile_carpet'] },
      { key: 'floor_ceramic', canvas: this.generateCeramicFloor(), aliases: ['tile_ceramic'] },

      // Walls
      { key: 'wall_back_left', canvas: this.generateBackLeftWall() },
      { key: 'wall_back_right', canvas: this.generateBackRightWall() },

      // Windows
      { key: 'window_day', canvas: this.generateWindowDay() },
      { key: 'window_sunset', canvas: this.generateWindowSunset() },
      { key: 'window_night', canvas: this.generateWindowNight() },

      // Multi-tier Furniture
      { key: 'bed_t1', canvas: this.generateBedT1() },
      { key: 'bed_t2', canvas: this.generateBedT2() },
      { key: 'bed_t3', canvas: this.generateBedT3() },

      { key: 'fridge_t1', canvas: this.generateFridgeT1() },
      { key: 'fridge_t2', canvas: this.generateFridgeT2() },
      { key: 'fridge_t3', canvas: this.generateFridgeT3() },

      { key: 'desk_t1', canvas: this.generateDeskT1() },
      { key: 'desk_t2', canvas: this.generateDeskT2() },
      { key: 'desk_t3', canvas: this.generateDeskT3() },

      { key: 'sofa_t1', canvas: this.generateSofaT1() },
      { key: 'sofa_t2', canvas: this.generateSofaT2() },
      { key: 'sofa_t3', canvas: this.generateSofaT3() },

      // Houseplants
      { key: 'plant_cactus', canvas: this.generatePlantCactus(), aliases: ['plant_succulent'] },
      { key: 'plant_monstera', canvas: this.generatePlantMonstera(), aliases: ['plant_ficus'] },
      { key: 'plant_bonsai', canvas: this.generatePlantBonsai() },

      // 5 State Souvenirs
      { key: 'souvenir_ny_statue_liberty', canvas: this.generateSouvenirNY(), aliases: ['souvenir_ny_statue'] },
      { key: 'souvenir_ca_golden_gate', canvas: this.generateSouvenirCA() },
      { key: 'souvenir_tx_longhorn', canvas: this.generateSouvenirTX() },
      { key: 'souvenir_fl_rocket_flamingo', canvas: this.generateSouvenirFL(), aliases: ['souvenir_fl_rocket'] },
      { key: 'souvenir_wa_space_needle', canvas: this.generateSouvenirWA() },

      // Utility
      { key: 'tile_cursor', canvas: this.generateTileCursor() },
      { key: 'shadow_oval', canvas: this.generateShadowOval() },
    ];

    for (const item of textures) {
      if (!item.canvas) continue;

      if (scene.textures.exists(item.key)) {
        scene.textures.remove(item.key);
      }
      scene.textures.addCanvas(item.key, item.canvas);

      if (item.aliases) {
        for (const alias of item.aliases) {
          if (scene.textures.exists(alias)) {
            scene.textures.remove(alias);
          }
          scene.textures.addCanvas(alias, item.canvas);
        }
      }

      this.hydrateCanvasWithPng(item.canvas, item.key, () => {
        scene.textures.get(item.key)?.clearTintCache();
        if (item.aliases) {
          for (const alias of item.aliases) {
            scene.textures.get(alias)?.clearTintCache();
          }
        }
        scene.game?.markInteraction();
        scene.game?.render();
      });
    }

    // Register Animated Character Spritesheet
    const charCanvas = this.generateCharacterSpritesheet();
    if (charCanvas) {
      const charKey = 'player_chibi';
      if (scene.textures.exists(charKey)) {
        scene.textures.remove(charKey);
      }
      const charTexture = scene.textures.addCanvas(charKey, charCanvas);
      if (charTexture) {
        let frameIndex = 0;
        for (let row = 0; row < 4; row++) {
          for (let col = 0; col < 6; col++) {
            charTexture.add(frameIndex, 0, col * 16, row * 24, 16, 24);
            frameIndex++;
          }
        }
      }
      this.hydratePlayerSpritesheetWithPng(charCanvas, () => {
        scene.textures.get(charKey)?.clearTintCache();
        scene.game?.markInteraction();
        scene.game?.render();
      });
    }
  }

  /**
   * Generates a warm Cozy Gouache oak-pedestal base canvas (immediately hydrated by the 512x512 Gouache PNG).
   */
  public static generateAtlasSouvenirCanvas(
    logicalW: number = 28,
    logicalH: number = 28,
    accentHex: string = '#E5B04D'
  ): HTMLCanvasElement | null {
    return this.createSuperSampledCanvas(logicalW, logicalH, 4, (ctx, w, h, s) => {
      const outline = '#3B2314';
      const cx = w / 2;
      const baseTopY = h - 7 * s;
      // Warm oak pedestal base
      drawPoly(
        ctx,
        [
          [cx - 8 * s, baseTopY],
          [cx + 8 * s, baseTopY - 2 * s],
          [cx + 9 * s, h - 2 * s],
          [cx - 9 * s, h - 2 * s],
        ],
        '#B87442',
        outline,
        1.1 * s
      );
      // Brass plaque
      drawPoly(
        ctx,
        [
          [cx - 3.5 * s, baseTopY + 1.5 * s],
          [cx + 3.5 * s, baseTopY + 1.5 * s],
          [cx + 3.5 * s, baseTopY + 3.5 * s],
          [cx - 3.5 * s, baseTopY + 3.5 * s],
        ],
        '#F2C864',
        outline,
        0.8 * s
      );
      // Gouache centerpiece silhouette placeholder prior to async PNG hydration
      drawPoly(
        ctx,
        [
          [cx, 3 * s],
          [cx + 7 * s, baseTopY],
          [cx - 7 * s, baseTopY],
        ],
        accentHex,
        outline,
        1.1 * s
      );
    });
  }

  /**
   * Registers the cohesive hand-drawn room shell, wooden desk chair, cozy character,
   * and all 10 bespoke Regional/Atlas 2.5D Gouache State Souvenir illustrations for HomeScene
   * without altering the 38-key catalog of generateAll().
   */
  public static registerCozyDioramaAssets(scene: Phaser.Scene): void {
    if (!scene || !scene.textures) return;

    const cozyAssets: Array<{ key: string; gen: () => HTMLCanvasElement | null }> = [
      { key: 'cozy_room_shell', gen: () => this.generateCozyRoomShell() },
      { key: 'desk_chair_cozy', gen: () => this.generateDeskChairCozy() },
      { key: 'player_cozy', gen: () => this.generatePlayerCozy() },
      { key: 'player_cozy_walk1', gen: () => this.generatePlayerCozyWalk1() },
      { key: 'player_cozy_walk2', gen: () => this.generatePlayerCozyWalk2() },
      { key: 'player_cozy_back', gen: () => this.generatePlayerCozyBack() },
      { key: 'player_cozy_back_walk1', gen: () => this.generatePlayerCozyBackWalk1() },
      { key: 'player_cozy_back_walk2', gen: () => this.generatePlayerCozyBackWalk2() },
      { key: 'souvenir_az_grand_canyon', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#D96B43') },
      { key: 'souvenir_or_crater_lake', gen: () => this.generateAtlasSouvenirCanvas(28, 26, '#2D6FB7') },
      { key: 'souvenir_id_star_garnet', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#9E2A47') },
      { key: 'souvenir_nv_hoover_dam', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#C9B295') },
      { key: 'souvenir_co_rocky_peaks', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#7A9EC2') },
      { key: 'souvenir_mo_gateway_arch', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#B8C6D6') },
      { key: 'souvenir_hi_aloha_volcano', gen: () => this.generateAtlasSouvenirCanvas(28, 26, '#3AA876') },
      { key: 'souvenir_ak_aurora_totem', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#4CC9A6') },
      { key: 'souvenir_dc_capitol_dome', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#E6D6C8') },
      { key: 'souvenir_forest_smoky_mill', gen: () => this.generateAtlasSouvenirCanvas(28, 28, '#C86D3B') },
    ];

    for (const asset of cozyAssets) {
      if (scene.textures.exists(asset.key)) continue;
      const canvas = asset.gen();
      if (!canvas) continue;
      scene.textures.addCanvas(asset.key, canvas);
      this.hydrateCanvasWithPng(canvas, asset.key, () => {
        scene.textures.get(asset.key)?.clearTintCache();
        scene.game?.markInteraction();
        scene.game?.render();
      });
    }
  }

  /**
   * Returns a map of all synthesized canvases for testing and verification outside Phaser.
   */
  public static getCanvasMap(): Map<string, HTMLCanvasElement> {
    const map = new Map<string, HTMLCanvasElement>();
    const entries: Array<[string, HTMLCanvasElement | null]> = [
      ['floor_wood', this.generateWoodFloor()],
      ['floor_carpet', this.generateCarpetFloor()],
      ['floor_ceramic', this.generateCeramicFloor()],
      ['wall_back_left', this.generateBackLeftWall()],
      ['wall_back_right', this.generateBackRightWall()],
      ['window_day', this.generateWindowDay()],
      ['window_sunset', this.generateWindowSunset()],
      ['window_night', this.generateWindowNight()],
      ['player_chibi', this.generateCharacterSpritesheet()],
      ['bed_t1', this.generateBedT1()],
      ['bed_t2', this.generateBedT2()],
      ['bed_t3', this.generateBedT3()],
      ['fridge_t1', this.generateFridgeT1()],
      ['fridge_t2', this.generateFridgeT2()],
      ['fridge_t3', this.generateFridgeT3()],
      ['desk_t1', this.generateDeskT1()],
      ['desk_t2', this.generateDeskT2()],
      ['desk_t3', this.generateDeskT3()],
      ['sofa_t1', this.generateSofaT1()],
      ['sofa_t2', this.generateSofaT2()],
      ['sofa_t3', this.generateSofaT3()],
      ['plant_cactus', this.generatePlantCactus()],
      ['plant_monstera', this.generatePlantMonstera()],
      ['plant_bonsai', this.generatePlantBonsai()],
      ['souvenir_ny_statue_liberty', this.generateSouvenirNY()],
      ['souvenir_ca_golden_gate', this.generateSouvenirCA()],
      ['souvenir_tx_longhorn', this.generateSouvenirTX()],
      ['souvenir_fl_rocket_flamingo', this.generateSouvenirFL()],
      ['souvenir_wa_space_needle', this.generateSouvenirWA()],
      ['tile_cursor', this.generateTileCursor()],
      ['shadow_oval', this.generateShadowOval()],
    ];

    for (const [key, canvas] of entries) {
      if (canvas) {
        this.hydrateCanvasWithPng(canvas, key);
        map.set(key, canvas);
      }
    }
    return map;
  }

  /**
   * Generates the legacy flat-box (pre-2.5D vector overhaul) canvases for the 10 Pilot Items
   * so the Decor Pilot Showcase page can render authentic side-by-side Before vs After comparisons.
   */
  public static generateLegacyPilotCanvases(): Record<string, HTMLCanvasElement | null> {
    return {
      bed_t1: this.createSuperSampledCanvas(44, 26, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.warmBirch;
        ctx.beginPath();
        ctx.roundRect(2 * s, 12 * s, 40 * s, 12 * s, [2 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.midNavy;
        ctx.beginPath();
        ctx.roundRect(4 * s, 6 * s, 36 * s, 12 * s, [3 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.pureLinen;
        ctx.beginPath();
        ctx.roundRect(6 * s, 8 * s, 10 * s, 6 * s, [2 * s]);
        ctx.fill();
      }),
      bed_t2: this.createSuperSampledCanvas(48, 34, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.warmWalnut;
        ctx.fillRect(4 * s, 2 * s, 14 * s, 18 * s);
        ctx.fillStyle = PALETTE.classicOak;
        ctx.fillRect(4 * s, 16 * s, 40 * s, 14 * s);
        ctx.fillStyle = PALETTE.sunsetCoral;
        ctx.beginPath();
        ctx.roundRect(6 * s, 10 * s, 36 * s, 14 * s, [3 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.pureLinen;
        ctx.beginPath();
        ctx.roundRect(8 * s, 11 * s, 10 * s, 5 * s, [2 * s]);
        ctx.roundRect(8 * s, 17 * s, 10 * s, 5 * s, [2 * s]);
        ctx.fill();
      }),
      fridge_t1: this.createSuperSampledCanvas(22, 32, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.slateGray;
        ctx.beginPath();
        ctx.roundRect(2 * s, 2 * s, 18 * s, 28 * s, [3 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.frostWhite;
        ctx.beginPath();
        ctx.roundRect(3 * s, 4 * s, 16 * s, 24 * s, [2 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.coolCharcoal;
        ctx.fillRect(16 * s, 10 * s, 1.5 * s, 6 * s);
      }),
      fridge_t2: this.createSuperSampledCanvas(26, 44, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.coolCharcoal;
        ctx.beginPath();
        ctx.roundRect(2 * s, 2 * s, 22 * s, 40 * s, [3 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.frostWhite;
        ctx.fillRect(3 * s, 3 * s, 20 * s, 14 * s);
        ctx.fillRect(3 * s, 18 * s, 20 * s, 22 * s);
        ctx.fillStyle = PALETTE.slateGray;
        ctx.fillRect(20 * s, 7 * s, 1.5 * s, 8 * s);
        ctx.fillRect(20 * s, 21 * s, 1.5 * s, 10 * s);
      }),
      desk_t1: this.createSuperSampledCanvas(42, 30, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.coolCharcoal;
        ctx.fillRect(6 * s, 14 * s, 3 * s, 14 * s);
        ctx.fillRect(33 * s, 14 * s, 3 * s, 14 * s);
        ctx.fillStyle = PALETTE.warmBirch;
        ctx.beginPath();
        ctx.roundRect(2 * s, 10 * s, 38 * s, 6 * s, [2 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.slateGray;
        ctx.fillRect(16 * s, 4 * s, 10 * s, 7 * s);
        ctx.fillStyle = PALETTE.neonCyan;
        ctx.fillRect(17 * s, 5 * s, 8 * s, 5 * s);
      }),
      desk_t2: this.createSuperSampledCanvas(48, 36, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.warmWalnut;
        ctx.fillRect(6 * s, 18 * s, 4 * s, 16 * s);
        ctx.fillRect(38 * s, 18 * s, 4 * s, 16 * s);
        ctx.fillStyle = PALETTE.classicOak;
        ctx.beginPath();
        ctx.roundRect(2 * s, 14 * s, 44 * s, 6 * s, [2 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.deepInk;
        ctx.fillRect(10 * s, 4 * s, 12 * s, 9 * s);
        ctx.fillRect(24 * s, 4 * s, 12 * s, 9 * s);
        ctx.fillStyle = PALETTE.oceanTeal;
        ctx.fillRect(11 * s, 5 * s, 10 * s, 7 * s);
        ctx.fillRect(25 * s, 5 * s, 10 * s, 7 * s);
      }),
      sofa_t1: this.createSuperSampledCanvas(30, 26, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.sunsetCoral;
        ctx.beginPath();
        ctx.ellipse(15 * s, 14 * s, 12 * s, 10 * s, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = PALETTE.softBlush;
        ctx.beginPath();
        ctx.ellipse(13 * s, 11 * s, 7 * s, 5 * s, 0, 0, Math.PI * 2);
        ctx.fill();
      }),
      sofa_t2: this.createSuperSampledCanvas(46, 32, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.warmWalnut;
        ctx.fillRect(6 * s, 26 * s, 3 * s, 5 * s);
        ctx.fillRect(37 * s, 26 * s, 3 * s, 5 * s);
        ctx.fillStyle = PALETTE.midNavy;
        ctx.beginPath();
        ctx.roundRect(4 * s, 6 * s, 38 * s, 20 * s, [4 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.frostWhite;
        ctx.beginPath();
        ctx.roundRect(8 * s, 14 * s, 14 * s, 10 * s, [2 * s]);
        ctx.roundRect(24 * s, 14 * s, 14 * s, 10 * s, [2 * s]);
        ctx.fill();
      }),
      plant_t1: this.createSuperSampledCanvas(20, 24, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = HD_PALETTE.terracottaBase;
        ctx.beginPath();
        ctx.roundRect(5 * s, 14 * s, 10 * s, 9 * s, [2 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.vividGrass;
        ctx.beginPath();
        ctx.roundRect(8 * s, 4 * s, 4 * s, 11 * s, [2 * s]);
        ctx.fill();
      }),
      plant_t2: this.createSuperSampledCanvas(24, 32, 4, (ctx, _w, _h, s) => {
        ctx.fillStyle = PALETTE.paleCream;
        ctx.beginPath();
        ctx.roundRect(6 * s, 18 * s, 12 * s, 12 * s, [2 * s]);
        ctx.fill();
        ctx.fillStyle = PALETTE.oliveFern;
        ctx.beginPath();
        ctx.ellipse(8 * s, 11 * s, 6 * s, 4 * s, -0.4, 0, Math.PI * 2);
        ctx.ellipse(16 * s, 11 * s, 6 * s, 4 * s, 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = PALETTE.vividGrass;
        ctx.beginPath();
        ctx.ellipse(12 * s, 7 * s, 5 * s, 6 * s, 0, 0, Math.PI * 2);
        ctx.fill();
      }),
    };
  }
}

